import type {
  AppointmentStatus,
  AccessArea,
  AuditAction,
  AuditCategory,
  CancelPolicy,
  ClinicSection,
  ExpiryState,
  Gender,
  JournalType,
  MaritalStatus,
  MovementType,
  MedicalCondition,
  PriorInjectionType,
  PackagePaymentTerms,
  PatientPackageState,
  PaymentMethod,
  ReferralSource,
  ResourceKind,
  Role,
  ServiceColor,
  TreatmentArea,
  VisitStatus,
  WriteOffType,
} from '@clinic/contracts';
import { toPersianDigits } from '@clinic/money-date';

/** All UI text lives here (CLAUDE.md: Persian UI strings from one resource file). */
export const fa = {
  /** The parts of a clinic (ADR-036). */
  sections: {
    names: { general: 'کلینیک', laser: 'لیزر' } satisfies Record<ClinicSection, string>,
    all: 'همه',
    filter: 'بخش',
    laserPanel: 'بخش لیزر',
    account: { general: 'حساب کلینیک', laser: 'حساب لیزر' } satisfies Record<ClinicSection, string>,
  },
  app: {
    name: 'مدکسا',
    tagline: 'مدیریت آرام کلینیک زیبایی',
    loading: 'در حال بارگذاری…',
    retry: 'تلاش دوباره',
    loadMore: 'نمایش بیشتر',
    cancel: 'انصراف',
    save: 'ذخیره',
    offline: 'اتصال اینترنت برقرار نیست. تا برقراری دوباره‌ی اتصال، ثبت اطلاعات ممکن نیست.',
    unknownError: 'خطای غیرمنتظره‌ای رخ داد. دوباره تلاش کنید.',
    networkError: 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید.',
    traceId: 'کد پیگیری',
    notFound: 'صفحه‌ای با این نشانی پیدا نشد.',
    forbidden: 'اجازه‌ی دیدن این بخش را ندارید.',
    close: 'بستن',
    edit: 'ویرایش',
    add: 'افزودن',
    search: 'جست‌وجو',
    optional: 'اختیاری',
    spaceToBrowse: 'برای دیدن فهرست، کلید فاصله (Space) را بزنید.',
    saved: 'ذخیره شد.',
    toman: 'تومان',
    none: '—',
    back: 'بازگشت',
    archive: 'بایگانی',
    restore: 'خروج از بایگانی',
    confirm: 'تأیید',
    minutes: (n: string) => `${n} دقیقه`,
    testVersion: 'نسخه‌ی آزمایشی؛ داده‌ی این‌جا واقعی نیست.',
    updateReady: 'نسخه‌ی تازه‌ی برنامه آماده است. پس از تمام کردن کار فعلی، به‌روزرسانی کنید.',
    updateNow: 'به‌روزرسانی',
  },
  datePicker: {
    title: 'انتخاب تاریخ',
    gregorianTitle: 'تقویم میلادی',
    placeholder: 'انتخاب تاریخ',
    today: 'امروز',
    clear: 'پاک کردن',
    prevMonth: 'ماه قبل',
    nextMonth: 'ماه بعد',
    month: 'ماه',
    year: 'سال',
    count: (n: number) => `${toPersianDigits(n)} کلاینت نوبت دارند`,
  },
  nav: {
    clinics: 'کلینیک‌ها',
    gtins: 'بانک GTIN',
    defaultItems: 'فهرست پیش‌فرض کالا',
    insights: 'تحلیل انبار',
    calendar: 'تقویم',
    patients: 'کلاینت‌ها',
    sms: 'پیامک‌ها',
    inventory: 'انبار',
    finance: 'مالی',
    receivables: 'بدهکاران',
    settings: 'تنظیمات',
    more: 'بیشتر',
  },
  toast: {
    saved: 'ذخیره شد.',
    stockReceived: 'رسید انبار ثبت شد.',
  },
  theme: {
    modes: { system: 'مثل دستگاه', light: 'روشن', dark: 'تیره (شیفت شب)' },
    switch: (current: string, next: string) => `ظاهر: ${current}. برای «${next}» بزنید.`,
    panel: 'ظاهر و تم رنگی',
    modeTitle: 'روشن یا تیره',
    accentTitle: 'تم رنگی',
    accents: {
      petrol: 'سبز نفتی و شامپاینی',
      blue: 'آبی',
      rose: 'زرشکی',
      copper: 'مسی',
      graphite: 'گرافیتی',
      forest: 'سبز جنگلی',
      azure: 'آبی آسمانی',
      violet: 'بنفش',
      teal: 'سبز',
      pink: 'صورتی',
      amber: 'نارنجی',
    },
    customTitle: 'رنگ دلخواه',
    customHint: 'همه‌ی زمینه‌ها و سطح‌ها از همین رنگ ساخته می‌شوند و نوشته‌ها همیشه خوانا می‌مانند.',
    customPicker: 'انتخاب رنگ',
    previewTitle: 'پیش‌نمایش',
    previewCard: 'نوبت بعدی',
    previewAction: 'ثبت نوبت',
    previewSecondary: 'انصراف',
    reset: 'بازگشت به ظاهر پیش‌فرض',
    railCollapse: 'جمع کردن منو',
    railExpand: 'باز کردن منو',
    personal: 'فقط برای شما؛ روی این دستگاه با ورود دوباره برمی‌گردد و دیگران همان ظاهری را می‌بینند که خودشان انتخاب کرده‌اند.',
  },
  status: {
    appointment: {
      scheduled: 'رزرو شده',
      confirmed: 'تأیید شده',
      checked_in: 'پذیرش شد',
      in_progress: 'در حال انجام',
      completed: 'انجام شد',
      cancelled: 'لغو شد',
      no_show: 'نیامد',
    } satisfies Record<AppointmentStatus, string>,
    visit: { open: 'باز', completed: 'تکمیل شد', void: 'باطل شد' } satisfies Record<VisitStatus, string>,
    patientArchived: 'بایگانی‌شده',
  },
  home: {
    today: 'امروز',
    appointmentsToday: 'نوبت‌های امروز',
    nextUp: 'نوبت‌های پیش رو',
    callPatient: (name: string) => `تماس با ${name}`,
    noneToday: 'امروز نوبتی ندارید.',
    smsPending: (n: string) => `${n} پیامک در انتظار ارسال`,
    openCalendar: 'باز کردن تقویم',
    inventoryAlerts: (low: string, expiring: string) => `${low} کالا در کمبود · ${expiring} بچ نزدیک انقضا`,
    inventoryCalm: 'موجودی و انقضا در وضعیت عادی است.',
    todayTakings: 'دریافت امروز',
    myEarnings: 'درآمد من در این ماه',
    cardFailed: 'خوانده نشد.',
    newAppointment: 'نوبت تازه',
    takings: 'وجه دریافتی',
    revenueMonth: 'درآمد کارکرد',
    myShare: 'سهم کارکرد',
    financeOpen: 'مشاهده‌ی گزارش مالی',
    zero: 'صفر',
    followUp: 'نیازمند پیگیری',
    smsNone: 'پیامکی در انتظار ارسال نیست.',
    smsOpen: 'مشاهده‌ی صف ارسال',
    receivablesOpen: 'مشاهده‌ی بدهکاران',
    recallOpen: 'مشاهده‌ی فهرست موعد تمدید',
    inventoryOpen: 'مشاهده‌ی هشدارهای انبار',
    vendorsOpen: 'مشاهده‌ی بستانکاران',
    receivablesTitle: 'طلب از کلاینت‌ها',
    receivablesTotal: (amount: string, patients: string) => `${amount} از ${patients} کلاینت`,
    receivablesNone: 'هیچ کلاینتی بدهکار نیست.',
    overdue: (amount: string, patients: string) => `قسط سررسیدگذشته: ${amount} از ${patients} کلاینت`,
    andMore: (n: string) => `و ${n} مورد دیگر`,
    inventoryLow: 'کمبود',
    inventoryExpiring: 'رو به انقضا',
    recallTitle: 'موعد تمدید',
    recallOverdue: (n: string) => `گذشته از موعد: ${n} کلاینت`,
    recallComing: (n: string) => `نزدیک موعد: ${n} کلاینت`,
    recallNone: 'امروز کلاینتی در موعد تمدید نیست.',
    touchUpTitle: 'موعد ترمیم',
    touchUpOpen: 'مشاهده‌ی فهرست موعد ترمیم',
    touchUpNone: 'امروز کلاینتی در موعد ترمیم نیست.',
    costsTitle: 'قیمت خرید',
    costsPending: (n: string) => `${n} قلم کالا منتظر قیمت خرید`,
    costsNone: 'کالایی منتظر قیمت خرید نیست.',
    costsOpen: 'مشاهده‌ی منتظر قیمت خرید',
    missed: (n: string) => `نیامدند و لغوشده (${n})`,
    rebook: 'نوبت دوباره',
    views: { today: 'کار امروز', manage: 'مدیریت' },
    viewsLabel: 'نمای خانه',
    myAppointmentsToday: 'نوبت‌های من امروز',
    wholeClinic: 'همه‌ی کلینیک',
    nextPatient: 'کلاینت بعدی',
    nextAppointment: 'نوبت بعدی',
    serviceWith: (service: string, practitioner: string) => (practitioner ? `${service} با ${practitioner}` : service),
    noneNext: 'امروز نوبت دیگری ندارید.',
    clinicToday: 'امروز در کلینیک',
    clinicNoneToday: 'امروز نوبتی در کلینیک نیست.',
    clinicStats: { total: 'نوبت‌ها', done: 'انجام‌شده', open: 'مانده', missed: 'نیامد و لغو' },
    practitionersToday: 'درمانگران امروز',
    nextOf: 'بعدی:',
    noneLeft: 'نوبتی نمانده',
  },
  patients: {
    title: 'کلاینت‌ها',
    searchLabel: 'جست‌وجوی کلاینت',
    searchPlaceholder: 'نام، موبایل، کد ملی یا شماره‌ی پرونده',
    empty: 'هنوز کلاینتی ثبت نشده است.',
    noMatch: 'کلاینتی با این مشخصات پیدا نشد.',
    add: 'کلاینت تازه',
    showArchived: 'نمایش بایگانی‌شده‌ها',
    firstName: 'نام',
    lastName: 'نام خانوادگی',
    phone: 'شماره‌ی موبایل',
    nationalCode: 'کد ملی',
    nationalCodeHint: 'ده رقم؛ برای جست‌وجو و جلوگیری از پرونده‌ی تکراری.',
    fileNumber: 'شماره‌ی پرونده',
    fileNumberHint: 'اگر کلینیک برای کلاینت پرونده‌ی شماره‌دار دارد.',
    fileNumberShort: 'پرونده',
    notAvailable: 'ندارد',
    notAvailableFor: (field: string) => `${field}: ندارد`,
    exemptNationalCodeHint: 'بدون کد ملی ثبت می‌شود، مثلاً برای کلاینت خارجی.',
    gender: 'جنسیت',
    birthDate: 'تاریخ تولد',
    maritalStatus: 'وضعیت تأهل',
    maritalStatuses: { single: 'مجرد', married: 'متأهل' } satisfies Record<MaritalStatus, string>,
    address: 'نشانی',
    referralSource: 'نحوه‌ی آشنایی',
    notSet: 'انتخاب نشده',
    genders: { female: 'زن', male: 'مرد' } satisfies Record<Gender, string>,
    referralSources: {
      instagram: 'اینستاگرام',
      google: 'گوگل',
      website: 'وب‌سایت',
      referral_patient: 'معرفی کلاینت',
      referral_doctor: 'معرفی پزشک',
      walk_in: 'مراجعه‌ی حضوری',
      other: 'سایر',
    } satisfies Record<ReferralSource, string>,
    created: 'کلاینت ثبت شد.',
    updated: 'پرونده به‌روز شد.',
    archived: 'کلاینت بایگانی شد.',
    restored: 'کلاینت از بایگانی خارج شد.',
    archiveConfirm: 'بایگانی این کلاینت تأیید شود؟',
    archiveHint: 'کلاینت بایگانی‌شده در جست‌وجو و نوبت‌دهی نمی‌آید، ولی سابقه‌اش می‌ماند.',
    editTitle: 'ویرایش مشخصات',
    newTitle: 'کلاینت تازه',
    tabs: {
      overview: 'پرونده',
      medical: 'پروفایل پزشکی',
      notes: 'یادداشت‌ها',
    },
    /** The file's tabs, in the address as `?tab=`. */
    fileTabs: { summary: 'خلاصه', finance: 'مالی', history: 'سوابق', medical: 'پزشکی' },
    glance: {
      label: 'خلاصه‌ی کلاینت',
      balance: 'مانده',
      balanceIn: (section: string) => `مانده‌ی ${section}`,
      next: 'نوبت بعدی',
      last: 'آخرین مراجعه',
      recall: 'موعد تمدید',
      touchUp: 'موعد ترمیم',
      none: 'ندارد',
    },
    moreDetails: 'جزئیات بیشتر',
    lessDetails: 'جزئیات کمتر',
    history: { title: 'تاریخچه', empty: 'هنوز نوبت یا خدمتی ثبت نشده است.' },
    newAppointment: 'نوبت تازه',
    walkIn: 'ویزیت بدون نوبت',
    /** Columns of the patient list (docs/patient-list-booking/design.md). */
    list: {
      patient: 'کلاینت',
      lastVisit: 'آخرین مراجعه',
      nextAppointment: 'نوبت بعدی',
      balance: 'مانده',
      none: '—',
      at: (date: string, time: string) => `${date}، ساعت ${time}`,
      sessionsLeft: (n: string) => `پکیج: ${n} جلسه مانده`,
      call: (name: string) => `تماس با ${name}`,
      book: (name: string) => `نوبت تازه برای ${name}`,
    },
    listTabs: { all: 'همه', recall: 'موعد تمدید', touchUp: 'موعد ترمیم' },
    /** The recall list (BR-63, docs/recall-list/design.md). */
    recall: {
      overdue: 'موعد گذشته',
      soon: 'به‌زودی',
      window: (overdue: string, ahead: string | null) =>
        ahead
          ? `کسانی که موعد تمدید خدمتشان تا ${overdue} روز پیش گذشته یا تا ${ahead} روز دیگر می‌رسد و نوبت همان خدمت را ندارند.`
          : `کسانی که موعد تمدید خدمتشان تا ${overdue} روز پیش گذشته است و نوبت همان خدمت را ندارند.`,
      changeWindow: 'تغییر بازه',
      allServices: 'همه',
      serviceFilter: 'خدمت',
      emptyOverdue: 'در این بازه موعد تمدید کسی نگذشته است.',
      emptySoon: 'در این بازه موعد تمدید کسی نزدیک نیست.',
      noRules:
        'هیچ خدمتی «یادآوری تمدید» ندارد. در «تنظیمات ← خدمات» برای هر خدمت بنویسید پس از چند روز تمدید می‌شود.',
      lastTime: (date: string) => `آخرین بار: ${date}`,
      due: (date: string, when: string) => `موعد: ${date} (${when})`,
      daysAgo: (n: string) => `${n} روز پیش`,
      daysLeft: (n: string) => `${n} روز دیگر`,
      today: 'امروز',
      truncated: (n: string) => `فقط ${n} ردیف اول نشان داده شده است.`,
      book: (name: string, service: string) => `نوبت ${service} برای ${name}`,
      prior: 'از خدمت‌های قبلی',
      closeTick: (name: string) => `پیگیری ${name} انجام شد`,
      closeShort: 'پیگیری شد',
      closeTitle: 'پیگیری این کلاینت انجام شد؟',
      closeBody: 'با تأیید، «پیگیری شد» ثبت می‌شود و این کلاینت از لیست پیگیری‌ها و صف پیامک همین خدمت خارج می‌شود.',
      closeConfirm: 'بله، پیگیری شد',
    },
    /** The touch-up list (ADR-052): the same rows, on the touch-up date. */
    touchUp: {
      window: (overdue: string, ahead: string | null) =>
        ahead
          ? `کسانی که موعد ترمیم خدمتشان تا ${overdue} روز پیش گذشته یا تا ${ahead} روز دیگر می‌رسد و نوبت همان خدمت را ندارند.`
          : `کسانی که موعد ترمیم خدمتشان تا ${overdue} روز پیش گذشته است و نوبت همان خدمت را ندارند.`,
      emptyOverdue: 'در این بازه موعد ترمیم کسی نگذشته است.',
      emptySoon: 'در این بازه موعد ترمیم کسی نزدیک نیست.',
      noRules: 'هیچ خدمتی «ترمیم دارد» نیست. در «تنظیمات ← خدمات» کنار خدمتی که ترمیم دارد تیک بزنید.',
    },
    medical: {
      conditions: 'PMH (سابقه‌ی پزشکی)',
      otherConditions: 'سایر بیماری‌ها',
      allergies: 'حساسیت‌ها',
      medications: 'DH (سابقه‌ی دارویی)',
      notes: 'نکات پزشکی',
      notRecorded: 'ثبت نشده',
      editTitle: 'ویرایش پروفایل پزشکی',
      priorInjections: 'سابقه‌ی تزریق زیبایی',
      priorInjectionsNo: 'ندارد',
      priorInjectionsYes: 'دارد',
      priorInjectionTypes: 'کدام تزریق‌ها',
      priorInjectionsOther: 'سایر تزریق‌ها',
      empty: 'هنوز ثبت نشده است.',
      enter: 'ثبت پروفایل پزشکی',
      enterOrView: 'ثبت / مشاهده پروفایل پزشکی',
      draftTitle: 'پروفایل پزشکی',
      draftFilled: 'پروفایل پزشکی وارد شد؛ با ذخیره‌ی کلاینت ثبت می‌شود.',
      draftHint: 'می‌توانید همین حالا پر کنید یا بعداً از پرونده‌ی کلاینت.',
      draftDone: 'تأیید',
      draftRemove: 'حذف پروفایل واردشده',
      saveFailed: 'کلاینت ثبت شد، ولی پروفایل پزشکی ذخیره نشد. از پرونده‌ی کلاینت دوباره ثبت کنید.',
      openFile: 'رفتن به پرونده‌ی کلاینت',
      continueWith: 'ادامه با این کلاینت',
      saved: 'پروفایل پزشکی ذخیره شد.',
      audited: 'مشاهده و تغییر این بخش در گزارش رویدادها ثبت می‌شود.',
      none: 'هیچ‌کدام',
    },
    priorInjectionTypes: {
      botox: 'بوتاکس',
      filler: 'فیلر',
      mesotherapy: 'مزوتراپی',
      prp: 'PRP',
      thread: 'نخ',
      skin_booster: 'اسکین‌بوستر',
      fat_dissolver: 'چربی‌سوز',
      biostimulator: 'بیواستیمولاتور',
      endolift: 'اندولیفت',
      hifu: 'هایفو',
      rf: 'RF',
      pdrn_pn: 'PDRN / PN',
      exosome: 'اگزوزوم',
    } satisfies Record<PriorInjectionType, string>,
    conditions: {
      herpes: 'تبخال',
      diabetes: 'دیابت',
      hypertension: 'فشار خون',
      heart_disease: 'بیماری قلبی',
      thyroid: 'تیروئید',
      anemia: 'کم‌خونی',
      autoimmune: 'بیماری خودایمنی',
      epilepsy: 'صرع',
      hepatitis: 'هپاتیت',
      bleeding_disorder: 'اختلال انعقاد خون',
      pregnancy_or_breastfeeding: 'بارداری یا شیردهی',
    } satisfies Record<MedicalCondition, string>,
    notes: {
      add: 'یادداشت تازه',
      label: 'متن یادداشت بالینی',
      submit: 'ثبت یادداشت',
      empty: 'یادداشتی ثبت نشده است.',
      amend: 'اصلاح',
      amendOf: 'اصلاحِ یادداشت',
      amendedLater: 'اصلاح شده؛ یادداشت تازه‌تر را ببینید',
      appendOnly: 'یادداشت‌ها پاک یا ویرایش نمی‌شوند؛ اصلاح، یادداشت تازه‌ای است که به قبلی ارجاع می‌دهد.',
      created: 'یادداشت ثبت شد.',
    },
  },
  calendar: {
    title: 'تقویم',
    today: 'امروز',
    prevDay: 'روز قبل',
    nextDay: 'روز بعد',
    pickDay: 'رفتن به تاریخ',
    monthCalendar: 'تقویم ماه',
    noPractitioners: 'هنوز پزشکی عضو کلینیک نیست. از بخش اعضا، نقش پزشک را به یک عضو بدهید.',
    noServices: 'هنوز خدمتی تعریف نشده است. از تنظیمات، دست‌کم یک خدمت بسازید.',
    empty: 'برای این روز نوبتی ثبت نشده است.',
    closedDay: 'کلینیک در این روز تعطیل است. نوبت همچنان ثبت می‌شود، ولی در پیشنهاد زمان خالی نمی‌آید.',
    prevWeek: 'هفته‌ی قبل',
    nextWeek: 'هفته‌ی بعد',
    views: { label: 'نمای تقویم', agenda: 'فهرست', day: 'روز', week: 'هفته' },
    summary: {
      label: 'خلاصه‌ی روز',
      total: 'نوبت‌ها',
      unconfirmed: 'تأییدنشده',
      firstFree: 'زمان خالی بعدی',
      none: 'ندارد',
    },
    doctorsFilter: 'ستون‌های پزشکان',
    onlyBooked: 'فقط دارای نوبت',
    showAll: 'نمایش همه',
    now: 'اکنون',
    offGrid: (n: string) => `لغو و عدم مراجعه (${n})`,
    pickService: 'انتخاب خدمت',
    freeTimes: 'زمان‌های خالی',
    allTimes: (n: string) => `همه‌ی زمان‌ها (${n})`,
    dayParts: { morning: 'صبح', afternoon: 'بعدازظهر', evening: 'عصر' },
    otherTime: 'ساعت دیگر',
    moreOptions: 'مدت، اتاق و یادداشت',
    move: {
      title: 'جابه‌جایی این نوبت تأیید شود؟',
      to: (name: string, when: string) => `${name} به ${when}`,
      toDoctor: (name: string, when: string, doctor: string) => `${name} به ${when}، نزد ${doctor}`,
      confirm: 'جابه‌جا کن',
    },
    call: 'تماس',
    newAppointment: 'نوبت تازه',
    patient: 'کلاینت',
    practitioner: 'پزشک',
    service: 'خدمت',
    date: 'تاریخ',
    time: 'ساعت شروع',
    duration: 'مدت (دقیقه)',
    durationHint: (n: string) => `پیش‌فرض خدمت: ${n} دقیقه`,
    resources: 'اتاق و دستگاه',
    note: 'یادداشت نوبت (غیربالینی)',
    searchPatient: 'جست‌وجوی کلاینت با نام، شماره یا کد ملی',
    createPatientInline: (q: string) => (q ? `ثبت کلاینت تازه: «${q}»` : 'ثبت کلاینت تازه'),
    recentPatients: 'کلاینت‌ها اخیر',
    changePatient: 'تغییر کلاینت',
    overlap: 'این زمان با نوبت دیگری از همین پزشک تداخل دارد.',
    overrideReason: 'دلیل ثبت با وجود تداخل',
    allowsOverlap: 'این پزشک ثبت نوبت هم‌زمان را مجاز کرده است.',
    price: (amount: string) => `قیمت: ${amount}`,
    busySlot: (time: string) => `${time} (پر)`,
    slotBusy: 'این ساعت پر است؛ ساعت دیگری انتخاب کنید.',
    firstFree: (when: string) => `اولین زمان خالی: ${when}`,
    noFreeTime: 'تا ۳۱ روز آینده، در ساعت کاری کلینیک، زمان خالی پیدا نشد.',
    busyHint: 'ساعت‌های پر غیرفعال‌اند: نوبت‌های این پزشک و اتاق یا دستگاه انتخاب‌شده.',
    upcoming: 'نوبت‌های آینده‌ی این کلاینت',
    sameDay: (time: string, service: string) => `این کلاینت همین روز ساعت ${time} نوبت «${service}» دارد.`,
    likeLastTime: (date: string, what: string) => `مثل دفعه‌ی قبل (${date}): ${what}`,
    overlapNotAllowed: 'این پزشک در این زمان نوبت دارد و نوبت هم‌زمان را مجاز نکرده است. زمان دیگری انتخاب کنید یا خود پزشک در «تنظیمات ← نوبت‌دهی» اجازه بدهد.',
    book: 'ثبت نوبت',
    booked: 'نوبت ثبت شد.',
    commands: {
      confirm: 'تأیید نوبت',
      'check-in': 'پذیرش',
      cancel: 'لغو نوبت',
      'no-show': 'نیامد',
      start: 'ثبت خدمت و هزینه',
      reschedule: 'جابه‌جایی',
      openVisit: 'باز کردن ویزیت',
    },
    cancelReason: 'دلیل لغو',
    confirmCancel: 'لغو این نوبت تأیید شود؟',
    confirmNoShow: 'ثبت «نیامد» برای این نوبت تأیید شود؟',
    rescheduled: 'نوبت جابه‌جا شد.',
    rescheduleTitle: 'جابه‌جایی نوبت',
    overlapBadge: 'هم‌پوشان',
    walkInBadge: 'بدون نوبت',
    busyOther: 'مشغول، بخش دیگر',
    busyOtherHint: 'در این زمان نوبتی در بخش دیگر دارد.',
    noResources: 'اتاق یا دستگاهی تعریف نشده است.',
    requirement: (kind: string, category: string | null) => (category ? `${kind} (${category})` : kind),
    needs: 'نیاز این خدمت',
    at: (date: string, time: string) => `${date}، ساعت ${time}`,
    live: 'به‌روزرسانی زنده',
    openPatient: 'پرونده‌ی کلاینت',
    walkIn: {
      open: 'مراجعه‌ی بدون نوبت',
      title: 'مراجعه‌ی بدون نوبت',
      hint: 'کلاینت در اولین زمان آزاد امروزِ این پزشک (از همین حالا) ثبت می‌شود.',
      hintCheckIn: 'کلاینت در اولین زمان آزاد امروزِ این پزشک (از همین حالا) ثبت و پذیرش می‌شود و در صف «در انتظار» می‌آید.',
      submit: 'ثبت مراجعه',
      done: (time: string) => `ثبت شد، ساعت ${time}.`,
    },
  },
  today: {
    waiting: 'در انتظار',
    waitingNone: 'کسی منتظر نیست.',
    waitedFor: (minutes: string) => `${minutes} دقیقه منتظر`,
    waitedJustNow: 'همین الان رسید',
    actions: {
      checkIn: 'پذیرش',
      start: 'ثبت خدمت و هزینه',
      openVisit: 'ادامه‌ی ویزیت',
      pay: 'دریافت وجه',
    },
    started: 'ویزیت شروع شد.',
    checkedIn: (name: string) => `${name} پذیرش شد.`,
    paid: 'دریافت ثبت شد.',
  },
  visits: {
    title: 'ویزیت',
    /** Before/after photos are designed but not built yet (docs/clinical-media/design.md, ADR-048). */
    photos: {
      title: 'عکس قبل و بعد',
      upload: 'آپلود عکس (به‌زودی)',
    },
    lines: 'خدمات انجام‌شده',
    addLine: 'افزودن خدمت',
    service: 'خدمت',
    price: 'مبلغ برای این کلاینت (تومان)',
    priceHint: 'مبلغ هر کلاینت جدا وارد می‌شود. برای خدمت رایگان یا کم‌شده از پکیج، صفر بنویسید.',
    priceSave: 'ثبت مبلغ',
    priceMissing: 'مبلغ وارد نشده است.',
    priceUnsaved: (names: string) => `مبلغ ${names} هنوز ثبت نشده است. اول «ثبت مبلغ» را بزنید.`,
    total: 'جمع',
    noLines: 'هنوز خدمتی ثبت نشده است.',
    record: 'ثبت درمان',
    editRecord: 'ویرایش درمان',
    priceInvalid: 'مبلغ را به تومان و با عدد وارد کنید.',
    template: 'قالب درمان',
    saveRecord: 'ذخیره‌ی درمان',
    recordSaved: 'درمان ذخیره شد.',
    removeLine: 'حذف خدمت',
    removeConfirm: 'این خدمت از ویزیت حذف شود؟',
    complete: 'تکمیل ویزیت',
    completeTitle: 'تکمیل ویزیت: سه اثر',
    effects: { record: 'پرونده', inventory: 'انبار', finance: 'مالی' },
    effectRecord: (n: string) => `${n} خدمت در پرونده‌ی کلاینت ثبت و بسته می‌شود.`,
    effectNoRecord: 'بدون جزئیات درمان',
    effectNoMaterials: 'ماده‌ی مصرفی ثبت نشده؛ از انبار چیزی کم نمی‌شود.',
    effectUse: (qty: string, unit: string, item: string, lot: string) =>
      `${qty} ${unit} ${item}${lot} کم می‌شود`,
    effectLeftover: (qty: string, unit: string) =>
      `باقی‌مانده (${qty} ${unit}) به‌صورت ویال باز در انبار می‌ماند`,
    effectKept: 'و برای همین کلاینت نگه داشته می‌شود',
    effectProblem: {
      out_of_stock: 'موجودی این بچ کافی نیست',
      expired: 'این بچ منقضی شده است',
      reserved: 'این ویال برای کلاینت دیگری کنار گذاشته شده است',
      batch_gone: 'این بچ دیگر موجودی ندارد',
    },
    effectBlocked: 'تا این مشکل‌ها رفع نشود ویزیت تکمیل نمی‌شود.',
    materials: {
      title: 'مواد مصرفی',
      hint: 'تا تکمیل ویزیت از انبار چیزی کم نمی‌شود.',
      empty: 'ماده‌ای ثبت نشده است.',
      add: 'افزودن ماده',
      item: 'کالا',
      pickItem: 'جست‌وجوی کالا',
      batch: 'بچ پیشنهادی (FEFO)',
      noBatch: 'بچ قابل مصرفی برای این کالا نیست.',
      take: 'برداشت',
      used: 'مصرف واقعی',
      usedHint: 'عدد، اعشار یا کسر بنویسید: ۲، ۰٫۳ یا ۱/۳. یا یکی از دکمه‌های زیر را بزنید.',
      fractionHint: 'کسر را با خط کسری بنویسید، مثل ۱/۳ یا ۱ ۱/۲.',
      quickLabel: 'مقدار سریع',
      allTaken: 'همه‌ی برداشت',
      asDecimal: (v: string, unit: string) => `برابر ${v} ${unit}`,
      asDecimalRounded: (v: string, unit: string) => `برابر ${v} ${unit} (تا سه رقم اعشار گرد شد)`,
      keep: 'باقی‌مانده برای همین کلاینت نگه داشته شود',
      uid: 'شناسه‌ی رهگیری (اختیاری)',
      opened: 'ویال باز',
      sealed: 'دربسته',
      keptForThis: 'نگه‌داشته برای همین کلاینت',
      applied: 'کسر شد',
      remove: 'حذف ماده',
      removeConfirm: 'این ماده از ویزیت حذف شود؟',
      qtyInvalid: 'مقدار را با عدد، اعشار (حداکثر سه رقم) یا کسر مثل ۱/۳ بنویسید.',
      usedTooMuch: 'مصرف واقعی نمی‌تواند از برداشت بیشتر باشد.',
      summary: (take: string, used: string, unit: string) => `برداشت ${take} · مصرف ${used} ${unit}`,
    },
    effectCharge: (service: string, amount: string) => `${service}: ${amount} به حساب کلاینت اضافه می‌شود`,
    effectFree: (service: string) => `${service}: بدون مبلغ`,
    effectPackage: (service: string, name: string, session: string, sessions: string) =>
      `${service}: جلسه‌ی ${session} از ${sessions} پکیج «${name}»؛ مبلغی ثبت نمی‌شود`,
    effectBalance: (before: string, after: string) => `مانده‌ی کلاینت: ${before} ← ${after}`,
    effectDiscount: (amount: string) => `تخفیف: ${amount}`,
    discount: 'تخفیف (تومان)',
    discountGiven: 'تخفیف',
    discountHint: (max: string) => `حداکثر ${max}؛ در دفتر مالی به‌نام همین ویزیت ثبت می‌شود.`,
    discountInvalid: 'مبلغ تخفیف را درست بنویسید.',
    discountTooMuch: (max: string) => `تخفیف بیش از ${max} نمی‌شود.`,
    takePayment: 'ثبت دریافت',
    reverse: 'ابطال مالی ویزیت',
    reverseTitle: 'ابطال مالی ویزیت تکمیل‌شده',
    reverseHint: 'هزینه‌های این ویزیت برگشت می‌خورد و ویزیت باطل می‌شود. نوبتش لغو می‌شود تا آن زمان خالی بماند؛ پرونده دست نمی‌خورد.',
    reverseCharge: (service: string, amount: string) => `برگشت ${amount} بابت ${service}`,
    reverseSessions: (n: string) => `${n} جلسه به پکیج کلاینت برمی‌گردد`,
    reverseMaterials: (n: string) => `${n} ماده‌ی مصرفی به انبار برنمی‌گردد (مصرف شده است).`,
    reverseReason: 'دلیل ابطال',
    reversed: 'ویزیت باطل شد و هزینه‌هایش برگشت خورد.',
    completed: 'ویزیت تکمیل شد.',
    visitCompletedPaymentFailed: 'ویزیت تکمیل شد ولی دریافت ثبت نشد.',
    retryPayment: 'ثبت دوباره‌ی دریافت',
    payFromAccount: 'ثبت از حساب کلاینت',
    followUp: {
      title: 'موعد تمدید و ترمیم',
      hint: 'از تنظیمات خدمت و روز ترمیم پزشک پیشنهاد شده است؛ می‌توانید عوضش کنید یا پاکش کنید.',
      recallOn: 'موعد تمدید',
      touchUpOn: 'موعد ترمیم',
      none: 'ندارد',
      asTouchUp: 'این خدمت به‌تازگی انجام شده بود؛ این بار ترمیم آن حساب شد.',
      edit: 'ویرایش موعد',
      editTitle: (service: string) => `موعد تمدید و ترمیم ${service}`,
      saved: 'موعد ذخیره شد.',
      line: (label: string, date: string) => `${label}: ${date}`,
    },
    prior: {
      hint: 'خدمتی که کلاینت پیش‌تر در همین کلینیک یا جای دیگر گرفته است؛ فقط برای موعد تمدید و ترمیم، بی هزینه و انبار.',
      add: 'ثبت خدمت قبلی',
      service: 'خدمت',
      choose: 'انتخاب خدمت',
      performedOn: 'تاریخ انجام',
      performer: 'انجام‌دهنده',
      outside: 'بیرون از این کلینیک',
      note: 'یادداشت',
      datesHint: 'با انتخاب خدمت و تاریخ، موعدها پیشنهاد می‌شوند؛ می‌توانید عوضشان کنید.',
      saved: 'خدمت قبلی ثبت شد.',
      void: 'ابطال',
      voidTitle: 'ابطال خدمت قبلی',
      voidHint: 'از فهرست موعد تمدید و ترمیم بیرون می‌رود و در سابقه با دلیل می‌ماند.',
      voidReason: 'دلیل ابطال',
      voided: 'باطل‌شده',
      voidedDone: 'خدمت قبلی باطل شد.',
      done: (date: string) => `انجام: ${date}`,
      by: (name: string) => `با ${name}`,
    },
    /** Profit of each service, apart from the books (ADR-053). */
    profit: {
      title: 'سود خدمت',
      materialCost: 'هزینه‌ی مواد',
      discount: 'تخفیف',
      profit: 'سود',
      total: 'سود ویزیت',
      incomplete: 'ناقص؛ قیمت خرید برخی مواد ثبت نشده است.',
      hint: 'مواد به قیمت خرید همان بچ. جدا از دفتر مالی، که هزینه‌ی مواد را هنگام خرید ثبت کرده است.',
      line: (label: string, amount: string) => `${label}: ${amount}`,
    },
    nextSession: 'نوبت جلسه‌ی بعد',
    nextSessionOn: (date: string) => `پیشنهاد: ${date}`,
    packageSessionsLeft: (n: string) => `${n} جلسه از پکیج مانده`,
    void: 'ابطال ویزیت',
    voidReason: 'دلیل ابطال',
    voidConfirm: 'ابطال این ویزیت تأیید شود؟ اگر از نوبت شروع شده باشد، نوبت لغو می‌شود.',
    voided: 'ویزیت باطل شد.',
    closed: 'این ویزیت بسته است و تغییر نمی‌کند.',
    walkIn: 'بدون نوبت',
    started: 'شروع',
    clinicalHidden: 'جزئیات درمان فقط برای پزشک نمایش داده می‌شود.',
    unit: { units: 'واحد', ml: 'میلی‌لیتر' },
    mapHint: 'ناحیه را انتخاب کنید و مقدار را وارد کنید.',
    patientRight: 'سمت راست کلاینت',
    muscleLegend: 'راهنمای عضلات',
    mapTotal: (v: string, unit: string) => `جمع: ${v} ${unit}`,
    amountFor: (area: string) => `مقدار برای ${area}`,
    clearArea: 'پاک کردن',
    noDetails: 'درمانی ثبت نشده است.',
    points: {
      add: 'نقطه‌ی تازه',
      placing: 'روی چهره، جای نقطه را لمس کنید.',
      cancelPlacing: 'انصراف از نقطه‌گذاری',
      defaultLabel: (n: string) => `نقطه‌ی ${n}`,
      name: 'نام نقطه',
      nameFor: (label: string) => `نام ${label}`,
      marker: (label: string) => `نقطه‌ی دلخواه: ${label}`,
      moveHint: 'برای جابه‌جایی دقیق، نقطه را انتخاب کنید و کلیدهای جهت را بزنید.',
      limit: (n: string) => `حداکثر ${n} نقطه‌ی دلخواه ثبت می‌شود.`,
      custom: 'دلخواه',
    },
  },
  methods: {
    title: 'متدهای من',
    open: 'متدها',
    hint: 'نقاط و مقدارهای یک جلسه را با یک نام ذخیره کنید تا برای زیباجوی بعدی دوباره وارد نشوند. هر متد برای خانم و آقا مقدار جدا دارد.',
    empty: 'هنوز متدی ذخیره نکرده‌اید. پس از پر کردن نقشه، «ذخیره به‌عنوان متد» را بزنید.',
    use: 'استفاده',
    useFor: (gender: string) => `استفاده (نسخه‌ی ${gender})`,
    applied: (name: string) => `متد «${name}» در نقشه گذاشته شد؛ پیش از ذخیره می‌توانید تغییرش دهید.`,
    otherVariant: (has: string, wanted: string) => `این متد فقط نسخه‌ی ${has} دارد، نه ${wanted}.`,
    replaceConfirm: 'نقشه‌ی فعلی با مقدارهای این متد جایگزین شود؟',
    replace: 'جایگزین کن',
    save: 'ذخیره به‌عنوان متد',
    saveTitle: 'ذخیره‌ی نقشه به‌عنوان متد',
    saveEmpty: 'اول دست‌کم یک ناحیه یا نقطه با مقدار معتبر ثبت کنید.',
    summary: (count: string, total: string) => `${count} ناحیه و نقطه؛ ${total}`,
    target: 'ذخیره در',
    newMethod: 'متد تازه',
    name: 'نام متد',
    gender: 'برای',
    genderHint: 'مقدارها برای این جنسیت ذخیره می‌شوند؛ برای جنسیت دیگر جدا ذخیره کنید.',
    genderRequired: 'جنسیت کلاینت ثبت نشده است؛ انتخاب کنید این مقدارها برای کدام است.',
    willReplace: (gender: string) => `نسخه‌ی ${gender} این متد با نقشه‌ی فعلی جایگزین می‌شود.`,
    willAdd: (gender: string) => `نسخه‌ی ${gender} به این متد اضافه می‌شود.`,
    description: 'توضیح متد',
    referenceUrl: 'لینک توضیحات',
    referenceUrlHint: 'نشانی کامل اینترنتی که با https شروع می‌شود.',
    saved: 'متد ذخیره شد.',
    details: 'توضیحات متد',
    openReference: 'باز کردن لینک توضیحات',
    noDescription: 'توضیحی ثبت نشده است.',
    system: 'پیشنهاد سیستم',
    variants: { female: 'خانم', male: 'آقا' } satisfies Record<Gender, string>,
    noVariant: 'ثبت نشده',
    total: (v: string, unit: string) => `${v} ${unit}`,
    edit: 'ویرایش نام و توضیح',
    archive: 'بایگانی متد',
    archiveConfirm: 'این متد بایگانی شود؟ پرونده‌ی کلاینت‌هایی که با آن ثبت شده‌اند تغییری نمی‌کند.',
    archived: 'متد بایگانی شد.',
    updated: 'متد به‌روز شد.',
    privateNote: 'متدهای هر پزشک فقط برای خود او دیده می‌شوند.',
  },
  areas: {
    forehead: 'پیشانی',
    glabella: 'بین ابرو',
    crows_feet_right: 'کنار چشم راست',
    crows_feet_left: 'کنار چشم چپ',
    bunny_lines: 'چین بینی',
    lip_flip: 'لیپ فلیپ',
    chin: 'چانه',
    masseter_right: 'ماستر راست',
    masseter_left: 'ماستر چپ',
    neck: 'گردن',
    lips_upper: 'لب بالا',
    lips_lower: 'لب پایین',
    nasolabial_right: 'خط خنده راست',
    nasolabial_left: 'خط خنده چپ',
    marionette_right: 'ماریونت راست',
    marionette_left: 'ماریونت چپ',
    cheek_right: 'گونه راست',
    cheek_left: 'گونه چپ',
    tear_trough_right: 'زیر چشم راست',
    tear_trough_left: 'زیر چشم چپ',
    jawline_right: 'خط فک راست',
    jawline_left: 'خط فک چپ',
    temple_right: 'شقیقه راست',
    temple_left: 'شقیقه چپ',
    face_full: 'کل صورت',
    upper_lip: 'پشت لب',
    underarm: 'زیر بغل',
    arms_full: 'کل دست',
    forearms: 'ساعد',
    legs_full: 'کل پا',
    legs_lower: 'ساق پا',
    bikini: 'بیکینی',
    back: 'پشت',
    abdomen: 'شکم',
    chest: 'قفسه‌ی سینه',
  } satisfies Record<TreatmentArea, string>,
  inventory: {
    title: 'انبار',
    subtitle: 'موجودی از روی دفتر حرکت کالا محاسبه می‌شود؛ هر تغییر یک حرکت ثبت‌شده است.',
    tabs: { items: 'کالاها', receive: 'ورود کالا', costs: 'منتظر قیمت خرید', alerts: 'هشدارها', receipts: 'رسیدها', trace: 'ردیابی' },
    search: 'نام کالا یا اسکن بارکد…',
    showArchived: 'نمایش بایگانی‌شده‌ها',
    empty: 'هنوز کالایی تعریف نشده است.',
    noMatch: 'کالایی با این مشخصات پیدا نشد.',
    newItem: 'کالای تازه',
    editItem: 'ویرایش کالا',
    name: 'نام کالا',
    nameEn: 'نام انگلیسی',
    category: 'دسته',
    unit: 'واحد',
    lowThreshold: 'کمبود',
    lowThresholdHint: 'وقتی موجودی قابل مصرف به این مقدار یا کمتر برسد، کالا در هشدار «کمبود» می‌آید. صفر یعنی هشدار نده.',
    gtin: 'بارکد کالا (GTIN)',
    gtinHint: 'با اسکن برچسب، کالا خودکار پیدا می‌شود.',
    suggestionError: 'فهرست کالاهای رایج خوانده نشد.',
    saved: 'کالا ذخیره شد.',
    archived: 'کالا بایگانی شد.',
    restored: 'کالا از بایگانی خارج شد.',
    onHand: 'موجودی',
    usable: 'قابل مصرف',
    low: 'کمبود',
    nearestExpiry: 'نزدیک‌ترین انقضا',
    noExpiry: 'بدون انقضا',
    batches: 'بچ‌ها',
    noBatches: 'موجودی ندارد.',
    lot: 'Lot',
    noLot: 'بدون Lot',
    expiry: 'انقضا',
    opened: 'ویال باز',
    reservedFor: (name: string) => `نگه‌داشته برای ${name}`,
    units: (n: string) => `${n} برچسب اصالت`,
    movements: 'حرکت‌ها',
    noMovements: 'حرکتی ثبت نشده است.',
    movementTypes: {
      receipt: 'ورود',
      consumption: 'مصرف در ویزیت',
      open_split: 'باز شدن ویال',
      return_to_supplier: 'برگشت به تأمین‌کننده',
      damage: 'ضایعات',
      expiry_writeoff: 'خروج انقضا',
      adjustment: 'اصلاح موجودی',
    } satisfies Record<MovementType, string>,
    expiryStates: {
      expired: 'منقضی',
      alert: 'انقضای نزدیک',
      warn: 'رو به انقضا',
      ok: 'معتبر',
      none: 'بدون انقضا',
    } satisfies Record<ExpiryState, string>,
    daysLeft: (n: string) => `${n} روز مانده`,
    daysAgo: (n: string) => `${n} روز گذشته`,
    adjust: 'اصلاح موجودی',
    adjustDelta: 'تغییر (مثبت یا منفی)',
    adjustHint: 'مثلاً ‎-1‎ برای یک عدد کمتر از شمارش.',
    writeOff: 'خروج از انبار',
    writeOffType: 'نوع خروج',
    writeOffTypes: {
      damage: 'ضایعات',
      expiry_writeoff: 'انقضا',
      return_to_supplier: 'برگشت به تأمین‌کننده',
    } satisfies Record<WriteOffType, string>,
    qty: 'مقدار',
    reason: 'دلیل',
    reasonHint: 'در گزارش رویدادها می‌ماند.',
    corrected: 'موجودی اصلاح شد.',
    release: 'آزاد کردن ویال',
    releaseConfirm: 'این ویال دیگر برای این کلاینت نگه داشته نشود؟',
    released: 'ویال آزاد شد.',
    receive: {
      title: 'رسید ورود کالا',
      kind: 'نوع رسید',
      kindPurchase: 'خرید',
      kindOpening: 'موجودی اولیه (خرید نیست)',
      openingHint: 'موجودیِ از پیش داشته‌ی کلینیک. خرید حساب نمی‌شود و قیمت یا نسیه نمی‌گیرد؛ در دفتر مالی چیزی ثبت نمی‌شود.',
      hint: 'مقدار، Lot و انقضا را از روی خود کالا بنویسید؛ اسکن فقط پیش‌پر می‌کند.',
      scan: 'اسکن برچسب',
      scanHint: 'اسکنر دستی را روی این فیلد بزنید یا متن بارکد را بچسبانید.',
      camera: 'دوربین',
      cameraStop: 'بستن دوربین',
      cameraUnsupported: 'این مرورگر اسکن با دوربین ندارد؛ از اسکنر دستی یا تایپ استفاده کنید.',
      cameraDenied: 'دسترسی به دوربین داده نشد.',
      scanned: 'از برچسب خوانده شد؛ پیش از ثبت بررسی کنید.',
      scanUnknown: 'بارکد شناخته نشد.',
      scanNoItem: 'کالایی با این بارکد نیست؛ نام و واحد را کامل کنید تا همراه این رسید تعریف شود.',
      scanUid: (n: string) => `${n} برچسب اصالت اسکن شد`,
      uidAdded: 'برچسب اصالت به قلم اضافه شد.',
      uidDuplicate: 'این برچسب در همین رسید اسکن شده است.',
      item: 'کالا',
      pickItem: 'انتخاب کالا',
      commonItems: 'فهرست کالاهای رایج',
      defineNew: (name: string) => `تعریف «${name}» به‌عنوان کالای تازه`,
      cancelDraft: 'برگشت به جست‌وجوی کالا',
      draftMore: 'مشخصات بیشتر (اختیاری)',
      qty: 'مقدار',
      lot: 'Lot (از روی جعبه)',
      expiry: 'انقضا (میلادی، از روی جعبه)',
      expiryHint: 'مثل روی جعبه: 2027-05-31 یا 05/2027',
      expiryCalendar: 'انتخاب انقضا از تقویم میلادی',
      expiryInvalid: 'تاریخ انقضا را مثل روی جعبه بنویسید، مثلاً 2027-05-31 یا 05/2027.',
      expiryEcho: (jalali: string) => `برابر ${jalali}`,
      expiryPast: 'این تاریخ گذشته است؛ کالای منقضی مصرف نمی‌شود.',
      unitCost: 'بهای واحد (تومان، اختیاری)',
      unitCostHint: 'خالی بماند تا بعداً در «منتظر قیمت خرید» وارد شود.',
      noCost: 'قیمت خرید را مدیر یا پزشک بعداً در «منتظر قیمت خرید» وارد می‌کند.',
      uids: 'برچسب‌های اصالت',
      addLine: 'قلم دیگر',
      removeLine: 'حذف قلم',
      supplier: 'تأمین‌کننده (اختیاری)',
      reference: 'شماره‌ی فاکتور (اختیاری)',
      note: 'یادداشت (اختیاری)',
      submit: 'ثبت رسید',
      done: 'رسید ثبت شد و موجودی به‌روز شد.',
      duplicateUids: (uids: string) =>
        `این برچسب‌ها قبلاً در این کلینیک ثبت شده‌اند (احتمال تکرار یا جعل): ${uids}`,
    },
    /** Purchase prices given after the receipt (ADR-053). */
    costs: {
      hint: 'این قلم‌ها بی قیمت خرید وارد انبار شده‌اند. قیمت هر قلم یک بار ثبت می‌شود و همان روز به‌عنوان خرید مواد در دفتر مالی می‌آید.',
      empty: 'کالایی منتظر قیمت خرید نیست.',
      receipt: (date: string) => `رسید ${date}`,
      by: (name: string) => `ثبت: ${name}`,
      unitCost: (unit: string) => `بهای هر ${unit} (تومان)`,
      zeroHint: 'صفر یعنی رایگان؛ چیزی در دفتر ثبت نمی‌شود.',
      save: 'ثبت قیمت خرید',
      saved: 'قیمت خرید ثبت شد و خرید مواد در دفتر آمد.',
      fillOne: 'دست‌کم قیمت یک قلم را بنویسید.',
      more: (n: string) => `و ${n} قلم دیگر؛ پس از ثبت این‌ها نشان داده می‌شوند.`,
      badge: (n: string) => `${n} قلم منتظر قیمت خرید`,
    },
    /** The central GTIN catalog as the clinic sees it (ADR-055). */
    catalog: {
      fromCatalog: 'مشخصات از بانک مرکزی',
      standard: 'کالای استاندارد',
      standardHint: 'مشخصات رسمیِ این بارکد در بانک مرکزی. نام کالای شما خودکار عوض نمی‌شود.',
      brand: 'برند',
      model: 'مدل',
      content: 'حجم یا دوز',
      pack: 'بسته',
      packValue: (type: string, count: string) => (type && count ? `${type}، ${count} عدد` : type || (count ? `${count} عدد` : '')),
      useName: 'استفاده از نام استاندارد',
      nameUsed: 'نام استاندارد برای این کالا ذخیره شد.',
      nameSame: 'نام کالا همان نام استاندارد است.',
      statusPending: 'در انتظار بررسی بانک مرکزی',
      statusRejected: 'رد شد',
      statusWithdrawn: 'پس گرفته شد',
      rejectedReason: (reason: string) => `دلیل: ${reason}`,
      pendingHint: 'بارکد این کالا برای کلینیک شما کار می‌کند؛ پس از تأیید پلتفرم برای همه‌ی کلینیک‌ها استاندارد می‌شود.',
      complete: 'تکمیل مشخصات',
      completeHint: 'اختیاری؛ هرچه از روی جعبه می‌دانید بنویسید تا بررسی سریع‌تر شود.',
      lidocaine: 'لیدوکائین',
      lidocaineUnknown: 'نامشخص',
      lidocaineYes: 'دارد',
      lidocaineNo: 'ندارد',
      packType: 'نوع بسته',
      packCount: 'تعداد در بسته',
      packCountInvalid: 'تعداد در بسته یک عدد صحیح (۱ تا ۱۰۰۰) است.',
      saveDetails: 'ذخیره‌ی مشخصات',
      detailsSaved: 'مشخصات برای بررسی ذخیره شد.',
    },
    receipts: {
      opening: 'موجودی اولیه',
      costPending: 'منتظر قیمت خرید',
      empty: 'رسیدی ثبت نشده است.',
      lines: (n: string) => `${n} قلم`,
      noSupplier: 'بدون نام تأمین‌کننده',
    },
    alerts: {
      low: 'کمبود',
      lowLine: (usable: string, threshold: string, unit: string) =>
        `${usable} ${unit} (کمبود: ${threshold})`,
      expiring: 'انقضا',
      noneLow: 'کالایی در کمبود نیست.',
      noneExpiring: 'بچ نزدیک انقضا نیست.',
    },
    trace: {
      title: 'ردیابی تا کلاینت',
      hint: 'از روی Lot یا شناسه‌ی رهگیری برچسب، کلاینت‌هایی را که از آن مصرف کرده‌اند پیدا کنید.',
      byLot: 'کالا و Lot',
      byUid: 'شناسه‌ی رهگیری',
      lot: 'Lot',
      uid: 'شناسه‌ی رهگیری (۲۰ رقم)',
      find: 'یافتن',
      empty: 'مصرفی با این مشخصات ثبت نشده است.',
      consumed: (qty: string, unit: string) => `${qty} ${unit}`,
      uidLength: 'شناسه‌ی رهگیری باید ۲۰ رقم باشد.',
      itemRequired: 'کالا را انتخاب کنید.',
      lotRequired: 'Lot را بنویسید.',
    },
    settings: {
      title: 'هشدار انقضا',
      alertDays: 'هشدار قرمز (روز مانده)',
      warnDays: 'هشدار زرد (روز مانده)',
      hint: 'بچ منقضی یا با روزهای کمتر از هشدار قرمز، قرمز نمایش داده می‌شود.',
      saved: 'آستانه‌های هشدار ذخیره شد.',
    },
  },
  finance: {
    title: 'مالی',
    subtitle: 'همه‌ی اعداد از دفتر مالی می‌آیند؛ ثبت‌ها ویرایش یا حذف نمی‌شوند و اصلاح فقط با برگشت است.',
    tabs: {
      today: 'امروز',
      books: 'دفتر',
      expenses: 'هزینه‌ها',
      receivables: 'بدهکاران',
      providers: 'سرویس‌های پرداخت',
      vendors: 'بستانکاران',
      month: 'گزارش ماه',
      services: 'سود خدمات',
      practitioners: 'سهم اعضا',
    },
    types: {
      charge: 'خدمت',
      payment: 'دریافت',
      discount: 'تخفیف',
      patient_refund: 'بازپرداخت به کلاینت',
      expense: 'هزینه',
      material_purchase: 'خرید مواد',
      adjustment: 'اصلاح',
      opening_balance: 'مانده‌ی افتتاحیه',
      reversal: 'برگشت',
      vendor_payment: 'پرداخت به تأمین‌کننده',
      vendor_opening_balance: 'بدهی پیشین به تأمین‌کننده',
      provider_payment: 'دریافت از سرویس پرداخت',
      provider_fee: 'کارمزد و مالیات سرویس پرداخت',
      provider_settlement: 'وصول از سرویس پرداخت',
    } satisfies Record<JournalType, string>,
    methods: {
      cash: 'نقد',
      pos: 'کارت‌خوان',
      card_to_card: 'کارت به کارت',
      bank_transfer: 'انتقال بانکی',
    } satisfies Record<PaymentMethod, string>,
    amount: 'مبلغ (تومان)',
    amountInvalid: 'مبلغ را به تومان و با عدد بیشتر از صفر بنویسید.',
    method: 'روش دریافت',
    refundMethod: 'روش پرداخت',
    note: 'یادداشت (اختیاری)',
    reason: 'دلیل',
    reasonRequired: 'دلیل را بنویسید.',
    packageSale: 'فروش پکیج',
    packageCancel: 'لغو پکیج (بستانکاری)',
    /** The booking form: a package of the patient that still has a session of the chosen service. */
    packageLine: (name: string, left: string, total: string) => `پکیج «${name}»: ${left} از ${total} جلسه‌ی این خدمت مانده`,
    packageUntil: (date: string) => `، اعتبار تا ${date}`,
    reversed: 'برگشت خورده',
    reverse: 'برگشت',
    reverseTitle: 'این ثبت برگشت بخورد؟',
    reverseHint: 'یک ثبت تازه با اثر معکوس زده می‌شود؛ ثبت اصلی در دفتر می‌ماند.',
    reverseDone: 'ثبت برگشت خورد.',
    noEntries: 'ثبتی در این بازه نیست.',
    csvExport: {
      title: 'خروجی برای اکسل',
      hint: 'همه‌ی ثبت‌های دفتر در بازه‌ی انتخابی، به‌صورت فایل CSV. مبلغ‌ها به ریال‌اند و بازه حداکثر ۳۶۶ روز است.',
      secure: 'فایل نام کلاینت و مبلغ دارد؛ پیش از دریافت، رمز شما دوباره خواسته می‌شود.',
      button: 'دریافت فایل CSV',
      done: 'فایل ساخته و دریافت شد.',
    },
    allTypes: 'همه‌ی انواع',
    from: 'از',
    to: 'تا',
    total: 'جمع',
    account: {
      title: 'حساب کلاینت',
      balance: 'مانده',
      owes: 'بدهکار',
      credit: 'بستانکار',
      settled: 'تسویه',
      empty: 'هنوز ثبتی ندارد.',
      pay: 'ثبت دریافت',
      allEntries: 'مشاهده‌ی همه',
      discount: 'تخفیف',
      refund: 'بازپرداخت',
      adjust: 'اصلاح مانده',
      opening: 'مانده‌ی افتتاحیه',
      openingHint: 'بدهی پیشین کلاینت، فقط یک بار.',
      adjustHint: 'مثبت یعنی بدهی بیشتر، منفی یعنی بدهی کمتر (مثلاً ‎-50000‎).',
      payableHint: (amount: string) => `حداکثر ${amount}`,
      advanceHint: (amount: string) => `بدهی فعلی ${amount}. بیش از آن به‌عنوان بیعانه ثبت می‌شود و کلاینت بستانکار می‌شود.`,
      refundableHint: (amount: string) => `بستانکاری کلاینت: ${amount}`,
      paid: 'دریافت ثبت شد.',
      recorded: 'ثبت شد.',
      packages: 'پکیج‌ها',
      sellPackage: 'فروش پکیج',
      pickPackage: 'پکیج',
      noPackages: 'پکیج فعالی تعریف نشده است.',
      sold: 'پکیج فروخته شد و مبلغش به حساب کلاینت اضافه شد.',
      sessions: (used: string, total: string) => `${used} از ${total} جلسه`,
      expiresOn: (date: string) => `اعتبار تا ${date}`,
      cancelPackage: 'لغو پکیج',
      cancelPackageHint: 'ارزش جلسه‌های باقی‌مانده به بستانکاری کلاینت برمی‌گردد؛ بازپرداخت نقدی جداست.',
      cancelled: 'پکیج لغو شد.',
      packageStates: {
        active: 'فعال',
        used_up: 'تمام‌شده',
        expired: 'منقضی',
        cancelled: 'لغوشده',
      } satisfies Record<PatientPackageState, string>,
      loadMore: 'ثبت‌های قدیمی‌تر',
    },
    day: {
      revenue: 'درآمد',
      charges: 'خدمات انجام‌شده',
      discounts: 'تخفیف‌ها',
      payments: 'دریافت‌ها',
      refunds: 'بازپرداخت‌ها',
      vendorPayments: 'پرداخت به تأمین‌کننده‌ها',
      expenses: 'هزینه‌ها',
      materials: 'خرید مواد',
      profit: 'سود',
      netCash: 'خالص نقدی',
      visitsCharged: 'ویزیت‌های دارای هزینه',
      byMethod: 'دریافت به تفکیک روش',
      byProvider: 'دریافت به تفکیک سرویس پرداخت',
      providerPayments: 'دریافت‌های سرویس‌های پرداخت',
      providerSettlements: 'وصول از سرویس‌های پرداخت',
      providerFees: 'کارمزد سرویس‌های پرداخت',
      takings: 'دریافت‌های این روز',
      noTakings: 'دریافتی ثبت نشده است.',
      profitHint: 'سود = درآمد − خرید مواد − هزینه‌ها − کارمزد سرویس‌های پرداخت',
    },
    expenses: {
      add: 'ثبت هزینه',
      category: 'دسته',
      date: 'تاریخ هزینه',
      method: 'روش پرداخت (اختیاری)',
      recorded: 'هزینه ثبت شد.',
      list: 'هزینه‌ها و خرید مواد',
    },
    receivables: {
      empty: 'کلاینت بدهکاری نیست.',
      summary: (count: string, total: string) => `${count} کلاینت بدهکار · جمع ${total}`,
      lastEntry: (date: string) => `آخرین ثبت: ${date}`,
      title: 'بدهکاران',
      subtitle: 'کلاینت‌هایی که به کلینیک بدهکارند؛ برای پیگیری تماس بگیرید یا همین‌جا وجه را دریافت کنید.',
      sortLabel: 'ترتیب فهرست',
      sort: { balance: 'بیشترین بدهی', oldest: 'قدیمی‌ترین بدهی' },
      truncated: (shown: string) => `${shown} مورد اول نشان داده شده است.`,
      call: (name: string) => `تماس با ${name}`,
      nextDue: (date: string) => `قسط بعدی: ${date}`,
      overdue: (amount: string) => `سررسید گذشته: ${amount}`,
      onSchedule: 'طبق اقساط',
    },
    month: {
      prev: 'ماه قبل',
      next: 'ماه بعد',
      byCategory: 'هزینه به تفکیک دسته',
      days: 'روزها',
      noExpenses: 'هزینه‌ای در این ماه ثبت نشده است.',
    },
    services: {
      service: 'خدمت',
      charged: 'تعداد',
      packageSessions: 'جلسه‌ی پکیج',
      revenue: 'درآمد',
      estCost: 'هزینه‌ی تخمینی مواد',
      estProfit: 'سود تخمینی',
      materialCost: 'هزینه‌ی مواد',
      profit: 'سود',
      incomplete: 'ناقص',
      incompleteHint: '«ناقص»: قیمت خرید برخی مواد هنوز ثبت نشده است.',
      profitHint: 'سود = درآمد منهای تخفیف منهای مواد مصرفی به قیمت خرید؛ جدا از دفتر مالی.',
      hint: 'هزینه‌ی مواد اینجا تخمین هر خدمت است و فقط برای همین گزارش؛ سود کل از خرید واقعی مواد حساب می‌شود.',
      empty: 'خدمتی در این بازه انجام نشده است.',
    },
    practitioners: {
      member: 'پزشک',
      charged: 'خدمت',
      revenue: 'درآمد',
      share: 'سهم کارکرد',
      hint: 'درآمد شامل ارزش جلسه‌های پکیج است که خودش انجام داده.',
      empty: 'در این بازه خدمتی ثبت نشده است.',
      rules: 'قاعده‌های سهم کارکرد',
      rulesHint: 'قاعده‌ی تازه از همین لحظه جای قبلی را می‌گیرد؛ ویزیت‌های گذشته عوض نمی‌شوند.',
      addRule: 'قاعده‌ی تازه',
      ruleFor: 'برای',
      allServices: 'همه‌ی خدمات',
      percent: 'درصد',
      fixed: 'مبلغ ثابت (تومان)',
      ruleKind: 'نوع سهم',
      percentInvalid: 'درصد را بین ۰ تا ۱۰۰ با حداکثر دو رقم اعشار بنویسید.',
      current: 'جاری',
      previous: 'قبلی',
      noRules: 'قاعده‌ای تعریف نشده است؛ سهم صفر حساب می‌شود.',
      saved: 'قاعده ثبت شد.',
      bp: (percent: string) => `${percent}٪`,
    },
    packages: {
      title: 'پکیج‌های جلسات',
      hint: 'فروش پکیج کل مبلغ را به حساب کلاینت می‌آورد؛ هر جلسه‌ی همان خدمت بعداً خودکار از پکیج کم می‌شود.',
      add: 'پکیج تازه',
      name: 'نام پکیج',
      service: 'خدمت',
      sessions: 'تعداد جلسه',
      price: 'قیمت کل (تومان)',
      validDays: 'اعتبار (روز، اختیاری)',
      empty: 'پکیجی تعریف نشده است.',
      line: (sessions: string, price: string) => `${sessions} جلسه · ${price}`,
      valid: (days: string) => `اعتبار ${days} روز`,
      saved: 'پکیج ذخیره شد.',
      newTitle: 'پکیج تازه',
      editTitle: 'ویرایش پکیج',
      servicesTitle: 'خدمت‌ها',
      servicesHint: 'یک پکیج می‌تواند چند خدمت از یک بخش داشته باشد. جلسه‌ی هدیه رایگان است و بعد از جلسه‌های پولی مصرف می‌شود.',
      gift: 'جلسه‌ی هدیه',
      addService: 'افزودن خدمت',
      removeService: (n: string) => `حذف خدمت ${n}`,
      sessionsInvalid: 'تعداد جلسه‌ی هر خدمت را درست بنویسید (دست‌کم ۱).',
      percentInvalid: 'درصد را عددی بین ۰ تا ۱۰۰ بنویسید.',
      installmentsInvalid: 'حداکثر اقساط بین ۱ تا ۲۴ است.',
      daysInvalid: 'روزها را عدد بنویسید؛ سقف تمدید حداکثر ۳۶۵ روز است.',
      priceTitle: 'قیمت و تخفیف',
      listPrice: (amount: string) => `قیمت جلسه‌های پولی به نرخ فعلی: ${amount}`,
      pricing: 'نوع قیمت',
      pricingFixed: 'مبلغ ثابت',
      pricingDiscount: 'درصد تخفیف از قیمت خدمت‌ها',
      discountPercent: 'درصد تخفیف',
      discountHint: 'قیمت هر فروش از نرخ همان روز خدمت‌ها حساب می‌شود.',
      deskDiscount: 'سقف تخفیف منشی هنگام فروش (درصد)',
      deskDiscountHint: 'خالی یعنی منشی تخفیف نمی‌دهد. مدیر همیشه می‌تواند تخفیف بدهد.',
      paymentTitle: 'پرداخت',
      paymentTerms: 'شرایط پرداخت',
      terms: {
        any: 'به حساب کلاینت؛ هر وقت پرداخت کند',
        upfront: 'پرداخت کامل هنگام فروش',
        installments: 'پیش‌پرداخت و اقساط',
      } satisfies Record<PackagePaymentTerms, string>,
      minDown: 'حداقل پیش‌پرداخت (درصد)',
      maxInstallments: 'حداکثر تعداد قسط',
      cancelTitle: 'لغو و اعتبار',
      cancelPolicy: 'اگر کلاینت پکیج را لغو کند',
      policies: {
        pro_rata: 'ارزش جلسه‌های پولیِ مانده برمی‌گردد',
        none: 'پس از فروش، مبلغی برنمی‌گردد',
      } satisfies Record<CancelPolicy, string>,
      penalty: 'جریمه‌ی لغو (درصد)',
      penaltyHint: 'از مبلغ برگشتی کم می‌شود.',
      extension: 'حداکثر تمدید (روز)',
      extensionHint: 'منشی تا این تعداد روز اعتبار را تمدید می‌کند. خالی یعنی تمدید فقط با مدیر.',
      item: (service: string, sessions: string) => `${service}: ${sessions} جلسه`,
      itemWithGift: (service: string, sessions: string, gift: string) => `${service}: ${sessions} جلسه و ${gift} هدیه`,
      discounted: (price: string, percent: string) => `${price} (${percent}٪ تخفیف)`,
      installmentsShort: (n: string) => `تا ${n} قسط`,
    },
    visitPay: {
      title: 'پرداخت',
      planTitle: 'اقساط',
      due: (amount: string) => `مبلغ این ویزیت: ${amount}`,
      planDue: (amount: string) => `بدهی کلاینت که قسط‌بندی می‌شود: ${amount}`,
      how: 'نحوه‌ی پرداخت',
      modes: {
        full: 'تسویه‌ی کامل، همین حالا',
        partial: 'بخشی از مبلغ، باقی بدهی',
        installments: 'بخشی از مبلغ، باقی اقساط',
        debt: 'فعلاً پرداخت نمی‌کند، همه بدهی',
      },
      paidNow: 'مبلغ دریافتی (تومان)',
      downOptional: 'پیش‌پرداخت همین حالا (تومان، اختیاری)',
      rest: (amount: string) => `باقی‌مانده: ${amount}`,
      partialRange: 'مبلغ دریافتی باید بیشتر از صفر و کمتر از مبلغ ویزیت باشد.',
      fullHint: (amount: string) => `${amount} دریافت و حساب کلاینت صاف می‌شود.`,
      fullHintOwes: (amount: string, before: string) =>
        `${amount} دریافت و بدهی این ویزیت صاف می‌شود؛ بدهی قبلی کلاینت (${before}) می‌ماند.`,
      fullHintCredit: (amount: string, before: string) =>
        `${amount} دریافت و بدهی این ویزیت صاف می‌شود؛ بستانکاری قبلی کلاینت (${before}) سر جایش می‌ماند.`,
      debtHint: (amount: string) => `${amount} به‌عنوان بدهی کلاینت می‌ماند؛ بعداً با «دریافت وجه» ثبت می‌شود.`,
      doneFull: 'ویزیت تکمیل و پرداخت ثبت شد.',
      doneDebt: 'ویزیت تکمیل شد؛ مبلغ آن بدهی کلاینت است.',
      donePlan: 'ویزیت تکمیل شد و اقساط ثبت شد.',
      paymentFailed: 'ویزیت تکمیل شد، ولی پرداخت ثبت نشد. با «دریافت وجه» دوباره ثبت کنید.',
      planFailed: 'ویزیت تکمیل و پرداخت ثبت شد، ولی اقساط ثبت نشد. با «ثبت اقساط» دوباره امتحان کنید.',
      planAction: 'ثبت اقساط',
      planSaved: 'اقساط ثبت شد.',
      planCard: 'اقساط کلاینت',
      noPayer: 'پرداخت را منشی یا مدیر با «دریافت وجه» ثبت می‌کند.',
    },
    sale: {
      discount: 'تخفیف (تومان، اختیاری)',
      net: (amount: string) => `مبلغ پکیج پس از تخفیف: ${amount}`,
      upfrontHint: 'این پکیج هنگام فروش کامل پرداخت می‌شود.',
      down: 'پیش‌پرداخت (تومان)',
      downOptional: 'پرداخت همین حالا (تومان، اختیاری)',
      minDown: (amount: string) => `دست‌کم ${amount}`,
      method: 'روش پرداخت',
      installments: 'اقساط',
      count: 'تعداد قسط',
      firstDue: 'سررسید قسط اول',
      installmentNo: (n: string, date: string) => `قسط ${n}، ${date}`,
      cancelProRata: 'در صورت لغو، ارزش جلسه‌های پولیِ مانده برمی‌گردد.',
      cancelWithPenalty: (percent: string) => `در صورت لغو، ارزش جلسه‌های پولیِ مانده با ${percent}٪ جریمه برمی‌گردد.`,
      itemUse: (service: string, used: string, total: string) => `${service}: ${used} از ${total} جلسه`,
      withGift: (gift: string) => `(${gift} هدیه)`,
      noExpiry: 'بدون تاریخ انقضا',
      extended: (days: string) => `(${days} روز تمدید شده)`,
      nextInstallment: (date: string, amount: string) => `قسط بعدی: ${date}، ${amount}`,
      installmentsDone: (n: string) => `${n} قسط؛ سررسید همه گذشته است`,
      extend: 'تمدید',
      extendTitle: 'تمدید اعتبار پکیج',
      extendHint: (now: string, latest: string) => `اعتبار فعلی تا ${now} است و حداکثر تا ${latest} تمدید می‌شود.`,
      newExpiry: 'اعتبار تا',
      extendedDone: (date: string) => `اعتبار پکیج تا ${date} تمدید شد.`,
      cancelNone: 'با شرایط این پکیج، با لغو مبلغی به حساب کلاینت برنمی‌گردد.',
      cancelCredit: (amount: string, sessions: string, penalty: string | null) =>
        penalty
          ? `${amount} بابت ${sessions} جلسه‌ی پولیِ مانده، پس از ${penalty}٪ جریمه، به بستانکاری کلاینت برمی‌گردد؛ بازپرداخت نقدی جداست.`
          : `${amount} بابت ${sessions} جلسه‌ی پولیِ مانده به بستانکاری کلاینت برمی‌گردد؛ بازپرداخت نقدی جداست.`,
    },
    vendors: {
      title: 'تأمین‌کننده',
      add: 'تأمین‌کننده‌ی تازه',
      edit: 'ویرایش تأمین‌کننده',
      name: 'نام',
      phone: 'تلفن (اختیاری)',
      saved: 'تأمین‌کننده ذخیره شد.',
      empty: 'تأمین‌کننده‌ای ثبت نشده است. خرید نسیه یا قسطی را با تأمین‌کننده‌اش ثبت کنید تا بدهی و سررسیدها اینجا بیاید.',
      showArchived: 'نمایش بایگانی‌شده‌ها',
      total: (amount: string) => `بدهی کل کلینیک: ${amount}`,
      overdue: (amount: string) => `سررسید گذشته: ${amount}`,
      nextDue: (date: string) => `قسط بعدی: ${date}`,
      balance: 'بدهی کلینیک به این تأمین‌کننده',
      pay: 'ثبت پرداخت',
      paid: 'پرداخت به تأمین‌کننده ثبت شد.',
      opening: 'بدهی پیشین',
      openingHint: 'بدهی‌ای که پیش از شروع کار با برنامه به این تأمین‌کننده داشتید؛ فقط یک بار.',
      openingSaved: 'بدهی پیشین ثبت شد.',
      installments: 'اقساط',
      statement: 'گردش حساب',
      noEntries: 'هنوز ثبتی ندارد.',
      added: (amount: string) => `+ ${amount}`,
      lowered: (amount: string) => `− ${amount}`,
      onCredit: 'نسیه یا قسطی از تأمین‌کننده',
      vendor: 'تأمین‌کننده',
      noneYet: 'ابتدا در «مالی ← بستانکاران» تأمین‌کننده را تعریف کنید.',
      installmentCount: 'تعداد قسط',
      noInstallments: 'بدون قسط',
      dueNow: 'بدون قسط یعنی سررسید همین حالا.',
      firstDue: 'سررسید قسط اول',
      homeTitle: 'بدهی به تأمین‌کننده‌ها',
      from: (name: string) => `نسیه از ${name}`,
    },
    providers: {
      title: 'سرویس‌های پرداخت',
      subtitle: 'سرویس‌های پرداخت (اسنپ‌پی، دیجی‌پی، تارا و…)، طلب کلینیک از آن‌ها و ثبت وصول واقعی.',
      groupLabel: 'سرویس‌های پرداخت',
      loadFailed: 'فهرست سرویس‌های پرداخت خوانده نشد.',
      created: 'سرویس پرداخت با موفقیت ایجاد شد.',
      renamed: 'نام سرویس تغییر کرد.',
      termsUpdated: 'شرایط سرویس پرداخت با موفقیت به‌روزرسانی شد.',
      archivedDone: 'سرویس پرداخت بایگانی شد.',
      restoredDone: 'سرویس پرداخت بازگردانی شد.',
      revision: (n: number) => `ویرایش ${n}`,
      feeLine: (p: string) => `کارمزد: ${p}٪`,
      fixedPlus: (amount: string) => `+ ${amount} ثابت`,
      taxLine: (p: string) => `مالیات: ${p}٪`,
      settleLine: (text: string) => `تسویه: ${text}`,
      openLine: (amount: string) => `مانده باز: ${amount}`,
      overdueLine: (amount: string) => `سررسید گذشته: ${amount}`,
      common: {
        active: 'فعال',
        archived: 'بایگانی‌شده',
        voided: 'برگشت‌خورده',
        client: 'کلاینت',
        optional: 'اختیاری',
        zero: '۰',
        stageNo: (n: string) => `مرحله ${n}`,
      },
      form: {
        editNotice: 'دریافت‌های قبلی با شرایط خودشان می‌مانند. ویرایش شرایط، نسخه‌ی تازه‌ای از قرارداد از امروز می‌سازد.',
        nameHint: 'برای دو قرارداد جدا با یک شرکت، دو نام جدا بنویسید.',
        namePlaceholder: 'مثلاً اسنپ‌پی',
        suggestions: 'پیشنهادها:',
        feeHint: 'اگر کارمزد ندارد، ۰ بنویسید.',
        feePlaceholder: 'مثلاً ۵ یا ۵٫۵',
        fixedFeeHint: 'اگر ندارد، ۰ بنویسید.',
        taxHint:
          'فقط روی خود کارمزد حساب می‌شود، نه روی مبلغ خدمت. اگر قرارداد فقط نرخ نهایی با مالیات دارد، همان را در کارمزد بنویسید و این را صفر بگذارید.',
        taxPlaceholder: 'مثلاً ۱۰ (ارزش افزوده)',
        kindDays: 'چند روز تقویمی پس از دریافت',
        kindMonths: 'در ماه‌های شمسی بعد (یک یا چند مرحله)',
        daysHint: 'مثلاً ۰ برای همان روز، یا ۳۰ برای یک ماه بعد',
        stagesCaption: 'مراحل تسویه و درصد وصول هر قسط',
        total: (p: string) => `جمع: ${p}٪`,
        stageNo: (n: string) => `مرحله ${n}`,
        monthOffsetHint: 'چند ماه بعد',
        dayHint: 'روز ماه',
        shareHint: 'درصد سهم',
        sharePlaceholder: 'مثلاً ۵۰',
        errMonths: 'فاصله ماه‌ها باید اکیداً صعودی و حداکثر ۲۴ باشد.',
        errDay: 'روز ماه باید بین ۱ تا ۳۱ باشد.',
        errShare: 'سهم مرحله نامعتبر است.',
      },
      responsibility: (name: string) =>
        `فقط وقتی ثبت کنید که پرداخت را در پنل ${name} دیده و تأیید کرده‌اید. این ثبت پولی جابه‌جا نمی‌کند و پرداختی در ${name} انجام نمی‌دهد.`,
      submitPayment: (name: string) => `ثبت دریافت تأییدشده از ${name}`,
      refLabel: (name: string) => `شماره‌ی پیگیری در ${name} (اختیاری)`,
      viewInProviders: 'مشاهده در مطالبات سرویس‌ها',
      todayProviderTakingsHint: (amount: string) =>
        `${amount} از طریق سرویس‌های پرداخت؛ پس از وصول به دریافت افزوده می‌شود.`,
      preview: {
        title: 'پیش‌نمایش دریافت سرویس',
        gross: 'ناخالص',
        baseFee: 'کارمزد',
        feeTax: 'مالیات کارمزد',
        net: 'خالص طلب',
        stages: 'موعدهای تسویه',
      },
      newProvider: 'سرویس پرداخت تازه',
      editProvider: 'ویرایش شرایط سرویس',
      renameProvider: 'تغییر نام سرویس',
      name: 'نام سرویس',
      feeBp: 'کارمزد درصدی',
      fixedFee: 'کارمزد ثابت (تومان)',
      feeTaxBp: 'مالیات بر ارزش افزوده کارمزد (درصد)',
      scheduleKind: 'زمان تسویه‌ی سرویس با کلینیک',
      scheduleDays: 'تسویه کامل پس از چند روز',
      scheduleStages: 'مرحله‌های تسویه در ماه‌های شمسی بعد',
      addStage: 'افزودن مرحله',
      removeStage: 'حذف مرحله',
      monthOffset: 'فاصله ماه (چند ماه بعد)',
      dayOfMonth: 'روز ماه (۱ تا ۳۱)',
      shareBp: 'سهم مرحله (درصد)',
      days: (d: number) => `${d} روز پس از دریافت`,
      stagesSummary: (count: string) => `${count} مرحله‌ی تسویه`,
      totalShare100: 'جمع سهم مرحله‌ها باید دقیقاً ۱۰۰٪ باشد.',
      archive: 'بایگانی',
      restore: 'بازیابی',
      archiveConfirm: 'این سرویس پرداخت بایگانی شود؟ در این صورت برای دریافت‌های تازه قابل انتخاب نخواهد بود ولی مطالبات قبلی قابل وصول می‌ماند.',
      restoreConfirm: 'این سرویس پرداخت دوباره فعال شود؟',
      noProviders: 'هیچ سرویس پرداختی تعریف نشده است.',
      showArchived: 'نمایش بایگانی‌شده‌ها',
      tabs: {
        list: 'سرویس‌ها',
        receivables: 'مطالبات باز',
        settlements: 'واریزهای ثبت‌شده',
      },
      receivables: {
        title: 'مطالبات از سرویس‌های پرداخت',
        empty: 'هیچ مطالبه‌ای در این وضعیت یافت نشد.',
        filterState: 'وضعیت مطالبه',
        stateOpen: 'باز و تسویه‌نشده',
        stateOverdue: 'سررسید گذشته',
        stateCollected: 'تسویه و وصول‌شده',
        stateCancelled: 'برگشت‌خورده',
        stateAll: 'همه',
        openRial: 'مانده طلب',
        overdueRial: 'سررسید گذشته',
        collectedRial: 'وصول‌شده',
        totalReceivable: 'مبلغ کل طلب',
        effectiveDue: 'موعد سررسید',
        changeDue: 'تغییر موعد',
        newDueOn: 'موعد جدید',
        reason: 'علت تغییر موعد',
        dueChangedDone: 'موعد طلب با موفقیت تغییر کرد.',
        settleSelected: 'ثبت واریزی برای این طلب',
        receiptLink: 'مشاهده رسید دریافت',
        receiptNumber: 'شماره رسید',
        allProviders: 'همه سرویس‌ها',
        stageOf: (n: string) => `(مرحله ${n})`,
        partial: 'وصول بخشی',
        cancelledBadge: 'برگشت‌خورده',
        dueLabel: 'سررسید:',
        changedFrom: (d: string) => `(اصلاح‌شده؛ قبلی: ${d})`,
        receivedLabel: (d: string) => `دریافت: ${d}`,
        outstanding: (a: string) => `مانده: ${a}`,
        totalLine: (a: string) => `کل: ${a}`,
      },
      settlements: {
        title: 'واریزهای ثبت‌شده',
        newSettlement: 'ثبت واریز تازه از سرویس',
        empty: 'هیچ واریزی ثبت نشده است.',
        amount: 'مبلغ واریز (تومان)',
        receivedOn: 'تاریخ دریافت واریز',
        trackingRef: 'کد رهگیری بانکی (اختیاری)',
        allocationsTitle: 'تخصیص به مطالبات باز',
        allocationsHint: 'مبلغ واریزی را بین طلب‌های باز همین سرویس و بخش تقسیم کنید. جمع تخصیص‌ها باید دقیقاً برابر مبلغ واریز باشد.',
        allocAmount: 'مبلغ تخصیص (تومان)',
        remainingStage: 'مانده قسط',
        allocatedSum: 'جمع تخصیص‌یافته',
        mismatch: 'جمع مبالغ تخصیص‌یافته برابر با مبلغ کل واریز نیست.',
        saved: 'واریز از سرویس پرداخت ثبت شد.',
        void: 'برگشت واریز',
        voidTitle: 'این واریزی برگشت بخورد؟',
        voidHint: 'با برگشت واریزی، اثر نقدی آن معکوس شده و طلب‌های مربوطه دوباره باز می‌شوند.',
        voidDone: 'واریزی با موفقیت برگشت خورد.',
        amountPlaceholder: 'مبلغ واریزی به حساب',
        trackingPlaceholder: 'مثلاً ۱۲۳۴۵۶۷۸',
        suggestOldest: 'پیشنهاد: قدیمی‌ترین موعد اول',
        sumLabel: 'جمع تخصیص‌ها:',
        sumOf: (allocated: string, total: string) => `${allocated} از ${total}`,
        noOpen: 'هیچ طلب باز قابل وصولی برای این سرویس و بخش یافت نشد.',
        colDue: 'موعد',
        colTotal: 'مبلغ کل',
        colRemaining: 'مانده',
        dateLine: (d: string) => `واریز: ${d}`,
        trackingLine: (r: string) => `پیگیری: ${r}`,
        noteLine: (n: string) => `یادداشت: ${n}`,
        allocatedTo: (n: string) => `تخصیص به ${n} طلب:`,
      },
      receipts: {
        detailTitle: 'جزئیات رسید سرویس پرداخت',
        providerName: 'نام سرویس',
        gross: 'مبلغ ناخالص (پرداخت کلاینت)',
        baseFee: 'کارمزد پایه سرویس',
        feeTax: 'مالیات بر ارزش افزوده کارمزد',
        deductions: 'مجموع کسورات',
        net: 'مبلغ خالص واریزی به کلینیک',
        clientPaid: 'پرداخت‌شده توسط کلاینت',
        externalRef: 'کد پیگیری سرویس',
        status: 'وضعیت',
        void: 'برگشت دریافت',
        voidTitle: 'این دریافت برگشت بخورد؟',
        voidHint: 'در صورت برگشت، بدهی کلاینت دوباره بازگشته و مطالبات سرویس لغو می‌شوند. شرط برگشت این است که هیچ واریزیِ فعالی برای این رسید ثبت نشده باشد.',
        voidDone: 'دریافت با موفقیت برگشت خورد.',
        receivedOn: 'تاریخ دریافت',
        termsRevision: 'نسخه قرارداد',
        stageLine: (n: string, due: string) => `مرحله ${n} · موعد ${due}`,
        dueChanged: '(اصلاح‌شده)',
        collectedNote: (amount: string) => `(${amount} وصول‌شده)`,
      },
      paymentField: {
        label: 'سرویس پرداخت',
        selectProvider: 'انتخاب سرویس پرداخت...',
        externalRef: 'کد رهگیری سرویس پرداخت (اختیاری)',
        providerNotSelected: 'لطفاً سرویس پرداخت را انتخاب کنید.',
        refPlaceholder: 'مثلاً SP-9821034',
      },
    },
    settings: {
      title: 'بیعانه بدون پکیج',
      hint: 'اگر روشن باشد، منشی می‌تواند بیش از بدهی کلاینت دریافت کند. کلاینت بستانکار می‌شود و هزینه‌ی خدمت‌های بعدی خودکار از همان کم می‌شود.',
      advance: 'دریافت بیعانه و پیش‌پرداخت مجاز است',
    },
  },
  settings: {
    title: 'تنظیمات',
    tabs: {
      services: 'خدمات',
      resources: 'اتاق و دستگاه',
      packages: 'پکیج‌ها',
      sms: 'قالب‌های پیامک',
      inventory: 'انبار',
      booking: 'نوبت‌دهی',
      laser: 'بخش لیزر',
      data: 'خروجی داده',
    },
    dataSharing: {
      title: 'اشتراک داده‌ی انبار با پلتفرم',
      hint: 'پلتفرم فقط با مبنای قراردادیِ ثبت‌شده از آمار انبار شما استفاده می‌کند؛ هرگز کلاینت، ویزیت، قیمت یا تأمین‌کننده در آن نیست. تغییر آن با پلتفرم است.',
      level: 'وضعیت',
      levels: { none: 'خاموش', aggregate: 'فقط در جمع شبکه', detailed: 'با جزئیات کلینیک' },
      off: 'خاموش؛ آمار انبار این کلینیک استفاده نمی‌شود.',
      basis: 'مبنا',
      from: 'از تاریخ',
    },
    laser: {
      title: 'دسترسی بخش لیزر',
      hint: 'نوبت‌ها و حساب‌های لیزر از بخش کلینیک جدا هستند. اینجا تعیین می‌کنید کدام منشی با آن‌ها کار کند.',
      desk: 'نوبت‌ها، پذیرش و فهرست امروز لیزر با',
      cash: 'صندوق لیزر (مانده‌ی لیزر، دریافت وجه و فروش پکیج لیزر) با',
      choices: {
        laser_receptionist: 'منشی لیزر',
        receptionist: 'منشی کلینیک',
        both: 'هر دو',
      },
      always: 'مدیر و پزشک همه‌ی بخش‌ها را می‌بینند. اپراتور لیزر فقط ویزیت‌های لیزر را انجام و ثبت می‌کند.',
      howTo: 'نقش «منشی لیزر» و «اپراتور لیزر» را از بخش اعضا به افراد بدهید. خدمت لیزری را در «خدمات» با بخش «لیزر» تعریف کنید.',
      saved: 'دسترسی بخش لیزر ذخیره شد.',
      enabled: 'مرکز لیزر دارد',
      enabledHint:
        'خاموش یعنی هیچ چیز مربوط به لیزر (خدمات، نوبت‌ها، ویزیت‌ها، حساب و نقش‌های لیزر) برای هیچ‌کس نمایش داده یا ثبت نمی‌شود. چیزی پاک نمی‌شود و با روشن کردن دوباره همه برمی‌گردد.',
      off: 'این کلینیک بخش لیزر ندارد.',
      turnedOn: 'بخش لیزر روشن شد.',
      turnedOff: 'بخش لیزر خاموش شد.',
      confirmOffTitle: 'خاموش کردن بخش لیزر',
      confirmOff: 'این‌ها پنهان می‌شوند (پاک نمی‌شوند) تا دوباره روشن کنید:',
      confirmOffAction: 'خاموش کن',
      usage: {
        openAppointments: (n: string) => `${n} نوبت باز لیزر (یادآوری پیامکی‌شان هم فرستاده نمی‌شود)`,
        activePackages: (n: string) => `${n} پکیج فعال لیزر`,
        patientsWithBalance: (n: string) => `${n} کلاینت با مانده‌ی حساب لیزر`,
        services: (n: string) => `${n} خدمت لیزر`,
        members: (n: string) => `${n} عضو با نقش لیزر (نقش می‌ماند ولی به لیزر دسترسی ندارند)`,
        openProviderReceivables: (n: string) => `${n} طلب باز از سرویس‌های پرداخت در بخش لیزر`,
      },
    },
    booking: {
      title: 'نوبت هم‌زمان',
      hint: 'اگر روشن باشد، برای شما می‌شود در یک زمان بیش از یک نوبت ثبت کرد (مثلاً وقتی یک کلاینت زیر بی‌حسی است). فقط خود شما این را عوض می‌کنید.',
      allowOverlap: 'اجازه می‌دهم برای من نوبت هم‌زمان ثبت شود',
      devices: 'اتاق و دستگاه همیشه فقط یک نوبت در یک زمان دارند.',
      savedOn: 'از این پس نوبت هم‌زمان برای شما ثبت می‌شود.',
      savedOff: 'از این پس نوبت هم‌زمان برای شما ثبت نمی‌شود.',
      touchUpTitle: 'روز ترمیم',
      touchUpHint: 'برای خدمتی که «ترمیم دارد»، موعد ترمیم کلاینت چند روز پس از انجام آن به دست شماست. این عدد مال خود شماست.',
      touchUpDays: 'موعد ترمیم پس از (روز)',
      touchUpSaved: 'روز ترمیم ذخیره شد.',
      checkInTitle: 'پذیرش کلاینت',
      checkInHint: 'اگر روشن باشد، منشی کلاینتِ رسیده را «پذیرش» می‌کند، کلاینت در صف «در انتظار» با زمان انتظارش دیده می‌شود و پزشک فقط ویزیتِ پذیرش‌شده را شروع می‌کند. اگر خاموش باشد، پزشک ویزیت را مستقیم از نوبت شروع می‌کند.',
      checkInRequired: 'پیش از شروع ویزیت، پذیرش لازم است',
      checkInOn: 'پذیرش روشن شد: پزشک ویزیتِ پذیرش‌شده را شروع می‌کند.',
      checkInOff: 'پذیرش خاموش شد: پزشک ویزیت را مستقیم از نوبت شروع می‌کند.',
      recallTitle: 'فهرست موعد تمدید',
      recallHint:
        'در «کلاینت‌ها ← موعد تمدید» کسانی می‌آیند که موعد تمدید خدمتشان در این بازه است. موعد از «یادآوری تمدید پس از (روز)» هر خدمت حساب می‌شود.',
      recallOverdue: 'تا چند روز پس از موعد در فهرست بمانند (روز)',
      recallAhead: 'از چند روز پیش از موعد در فهرست بیایند (روز)',
      recallAheadHint: '۰ یعنی فقط وقتی موعدشان رسیده است.',
      recallSaved: 'بازه‌ی فهرست موعد تمدید ذخیره شد.',
    },
    services: {
      section: 'بخش',
      sectionHint: 'نوبت، ویزیت و حساب این خدمت در همین بخش ثبت می‌شود.',
      add: 'خدمت تازه',
      name: 'نام خدمت',
      duration: 'مدت (دقیقه)',
      price: 'قیمت (تومان)',
      materialCost: 'هزینه‌ی تخمینی مواد (تومان)',
      materialCostHint: 'فقط برای گزارش سود خدمت؛ هزینه‌ی واقعی مواد هنگام خرید ثبت می‌شود.',
      color: 'رنگ در تقویم',
      recall: 'یادآوری تمدید پس از (روز)',
      recallHint: 'خالی بگذارید تا یادآوری تمدید نداشته باشد.',
      recallAfter: (n: string) => `تمدید پس از ${n} روز`,
      touchUp: 'ترمیم دارد',
      touchUpHint: 'موعد ترمیم از «روز ترمیم» پزشکی حساب می‌شود که خدمت را انجام داده است.',
      touchUpTag: 'ترمیم دارد',
      template: 'قالب ثبت درمان',
      noTemplate: 'بدون قالب',
      requirements: 'نیاز به اتاق یا دستگاه',
      addRequirement: 'افزودن نیاز',
      category: 'دسته',
      categoryHint: 'مثلاً laser_alexandrite؛ باید با دسته‌ی دستگاه یکی باشد.',
      empty: 'هنوز خدمتی تعریف نشده است.',
      created: 'خدمت تعریف شد.',
      saved: 'خدمت ذخیره شد.',
      archived: 'خدمت بایگانی شد.',
      restored: 'خدمت از بایگانی خارج شد.',
      showArchived: 'نمایش بایگانی‌شده‌ها',
      newTitle: 'خدمت تازه',
      editTitle: 'ویرایش خدمت',
    },
    colors: {
      lilac: 'یاسی',
      rose: 'گلبهی',
      mint: 'نعنایی',
      sky: 'آسمانی',
      sand: 'شنی',
      clay: 'آجری',
    } satisfies Record<ServiceColor, string>,
    resources: {
      add: 'اتاق یا دستگاه تازه',
      name: 'نام',
      kind: 'نوع',
      kinds: { room: 'اتاق', device: 'دستگاه' } satisfies Record<ResourceKind, string>,
      category: 'دسته',
      categoryHint: 'برای تطبیق با نیاز خدمت، مثلاً laser_alexandrite.',
      empty: 'هنوز اتاق یا دستگاهی تعریف نشده است.',
      created: 'تعریف شد.',
      saved: 'ذخیره شد.',
      archived: 'بایگانی شد.',
      restored: 'از بایگانی خارج شد.',
      newTitle: 'اتاق یا دستگاه تازه',
      editTitle: 'ویرایش اتاق یا دستگاه',
    },
    smsChannels: {
      title: 'روش ارسال پیامک',
      device: 'سیم‌کارت همین دستگاه',
      deviceHint:
        'پیامک از برنامه‌ی پیام‌رسان گوشی و با تأیید شما فرستاده می‌شود. هزینه‌ای جز تعرفه‌ی سیم‌کارت ندارد.',
      operator: 'پنل پیامکی (API اپراتور)',
      operatorHint: 'ارسال مستقیم از سرور، بدون نیاز به گوشی. این روش هزینه دارد.',
      inUse: 'در حال استفاده',
      available: 'فعال',
      comingSoon: 'در آینده فعال خواهد شد',
      notEnabled: 'برای این کلینیک فعال نشده است',
    },
    sms: {
      body: 'متن پیامک',
      offsets: 'زمان ارسال',
      active: 'فعال',
      inactive: 'غیرفعال',
      customized: 'شخصی‌سازی‌شده',
      system: 'پیش‌فرض',
      placeholders: 'جای‌گذاری‌ها: {عنوان} {نام} {پروسیجر} {ساعت} {تاریخ} {کلینیک}',
      saved: 'قالب پیامک ذخیره شد.',
      untimed: 'فقط به انتخاب روی هر نوبت',
      recallNote: 'این پیامک در «موعد تمدید» هر خدمتِ انجام‌شده یا خدمت قبلیِ ثبت‌شده پیشنهاد می‌شود.',
      touchUpNote: 'این پیامک در «موعد ترمیم» هر خدمتِ انجام‌شده یا خدمت قبلیِ ثبت‌شده پیشنهاد می‌شود.',
      book: 'هنگام ثبت',
      offsetsAfter: 'زمان ارسال (بعد از پایان نوبت)',
      untimedAfter: 'ارسال خودکار نمی‌شود؛ فقط روی نوبتی که این قالب را انتخاب کرده باشد، آن هم با ساعتی که اینجا بنویسید.',
      hoursBefore: (n: number) => `${toPersianDigits(n)} ساعت قبل`,
      hoursAfter: (n: number) => `${toPersianDigits(n)} ساعت بعد`,
      customLabel: 'ساعت دلخواه',
      customBefore: 'چند ساعت قبل از نوبت؟',
      customAfter: 'چند ساعت بعد از نوبت؟',
      customAdd: 'افزودن',
      customRemove: (label: string) => `حذف ${label}`,
      customInvalid: (max: number) => `عددی صحیح از ۱ تا ${toPersianDigits(max)} بنویسید.`,
      customDuplicate: 'این زمان را قبلاً افزوده‌اید.',
      customTooMany: (max: number) => `حداکثر ${toPersianDigits(max)} زمان برای هر قالب.`,
    },
  },
  sms: {
    title: 'پیامک‌ها',
    subtitle: 'پیامک از سیم‌کارت همین دستگاه و با تأیید شما فرستاده می‌شود.',
    pending: 'در انتظار',
    done: 'ثبت‌شده',
    empty: 'پیامکی در صف نیست.',
    emptyDone: 'هنوز پیامکی ثبت نشده است.',
    send: 'ارسال',
    copy: 'کپی متن',
    copied: 'متن کپی شد.',
    skip: 'نفرستادن',
    confirmSentTitle: 'پیامک فرستاده شد؟',
    nextQueued: (name: string) => `ثبت شد. پیامک بعدی: ${name}`,
    confirmSentBody: 'اگر پیامک را از برنامه‌ی پیام‌رسان گوشی فرستادید، ثبتش کنید.',
    markSent: 'بله، فرستاده شد',
    confirmSkip: 'این پیامک فرستاده نشود؟',
    outcomes: { sent: 'ارسال شد', skipped: 'فرستاده نشد' },
    sentByOperator: 'ارسال شد (پنل پیامکی)',
    noPhone: 'شماره‌ی موبایل ثبت نشده',
    kinds: { appointment: 'نوبت', recall: 'تمدید' },
    categories: { all: 'همه', appointment: 'نوبت‌ها', recall: 'تمدید', repair_reminder: 'ترمیم' },
    closeFollowUp: 'پیگیری شد',
    closed: 'پیگیری شد',
    appointmentAt: (d: string) => `نوبت: ${d}`,
    dueAt: (d: string) => `موعد: ${d}`,
  },
  palette: {
    open: 'جست‌وجو و فرمان',
    shortcut: 'Ctrl K',
    placeholder: 'نام کلاینت، شماره، کد ملی یا یک فرمان…',
    patients: 'کلاینت‌ها',
    commands: 'فرمان‌ها',
    noResults: 'نتیجه‌ای پیدا نشد.',
    go: {
      calendar: 'رفتن به تقویم',
      patients: 'رفتن به کلاینت‌ها',
      sms: 'رفتن به پیامک‌ها',
      inventory: 'رفتن به انبار',
      finance: 'رفتن به مالی',
      receive: 'ثبت ورود کالا',
      settings: 'رفتن به تنظیمات',
      newPatient: 'ثبت کلاینت تازه',
      newAppointment: 'ثبت نوبت تازه',
      importPatients: 'ورود کلاینت‌ها از فایل',
      exportData: 'خروجی کامل داده',
      diagnostics: 'ضبط عیب‌یابی لمس',
    },
  },
  /** The touch recorder (shared/diag): timings only, never page text. */
  diag: {
    title: 'ضبط عیب‌یابی لمس',
    recording: (n: string) => `در حال ضبط: ${n} رویداد`,
    copy: 'کپی گزارش',
    stop: 'توقف',
    copied: 'گزارش کپی شد؛ آن را برای پشتیبانی بفرستید.',
    copyFailed: 'کپی نشد؛ متن زیر را نگه دارید و انتخاب کنید.',
  },
  auth: {
    title: 'ورود',
    subtitle: 'با شماره‌ی موبایل و رمز خود وارد شوید.',
    subtitleOtp: 'با شماره‌ی موبایل و کد پیامکی وارد شوید.',
    phone: 'شماره‌ی موبایل',
    phonePlaceholder: '۰۹۱۲ ۱۲۳ ۴۵۶۷',
    password: 'رمز',
    login: 'ورود',
    showPassword: 'نمایش رمز',
    hidePassword: 'پنهان کردن رمز',
    useOtp: 'ورود با کد پیامکی',
    usePassword: 'ورود با رمز',
    forgot: 'رمز را فراموش کرده‌اید؟ از مدیر کلینیک بخواهید برایتان لینک تازه بسازد.',
    sendCode: 'دریافت کد',
    code: 'کد تأیید',
    codeHint: (phone: string) => `کد ۶ رقمی ارسال‌شده به ${phone} را وارد کنید.`,
    verify: 'ورود',
    changePhone: 'تغییر شماره',
    resend: 'ارسال دوباره‌ی کد',
    resendIn: (seconds: string) => `ارسال دوباره تا ${seconds} ثانیه‌ی دیگر`,
    logout: 'خروج',
  },
  password: {
    setTitle: 'تعیین رمز',
    resetTitle: 'رمز تازه',
    /** Only the last four digits: one number reads the same in either direction. */
    forPhone: (last4: string) => `برای شماره‌ای که به ${last4} ختم می‌شود.`,
    validUntil: (when: string) => `این لینک تا ${when} معتبر است و فقط یک بار کار می‌کند.`,
    newPassword: 'رمز تازه',
    repeat: 'تکرار رمز',
    hint: 'دست‌کم ۱۰ نویسه. جمله‌ای کوتاه که فقط خودتان می‌دانید بهتر از یک کلمه با علامت است.',
    mismatch: 'دو رمز یکی نیستند.',
    submit: 'ذخیره‌ی رمز و ورود',
    noToken: 'این صفحه را فقط با لینکی که مدیر کلینیک داده باز کنید.',
    backToLogin: 'رفتن به صفحه‌ی ورود',
    changeTitle: 'تغییر رمز',
    current: 'رمز فعلی',
    changeSubmit: 'ذخیره‌ی رمز تازه',
    changed: 'رمز عوض شد. در دستگاه‌های دیگر باید دوباره وارد شوید.',
    firstTime: 'هنوز رمزی ندارید؛ رمز تازه را انتخاب کنید.',
  },
  platform: {
    system: {
      title: 'وضعیت سامانه',
      states: { ok: 'سالم', failed: 'ناهماهنگ', overdue: 'عقب افتاده', none: 'هنوز اجرا نشده' },
      reconciliation: 'هم‌خوانی شبانه‌ی دفترها و انبار',
      reconciliationAt: (when: string) => `آخرین بررسی: ${when}`,
      reconciliationOverdue: 'بیش از ۳۶ ساعت است که بررسی تازه‌ای ثبت نشده؛ کار زمان‌بندی‌شده را ببینید.',
      reconciliationNone: 'هنوز هیچ بررسی‌ای ثبت نشده است.',
      reconciliationFailed: 'این بررسی‌ها ناهماهنگی پیدا کردند (فقط تعداد؛ داده‌ی کلاینت و مبلغ اینجا نمی‌آید):',
      checks: {
        journal_source_missing: 'ثبت دفتر بدون منبع',
        journal_effects_mismatch: 'اثر نادرست ثبت دفتر',
        void_visit_charge_unreversed: 'هزینه‌ی ویزیتِ ابطال‌شده که برنگشته',
        package_overused: 'پکیجِ بیش از جلسه‌هایش مصرف‌شده',
        stock_cache_mismatch: 'موجودی انبار ناهم‌خوان با حرکت‌ها',
        vendor_overpaid: 'پرداخت به تأمین‌کننده بیش از بدهی',
      } as Record<string, string>,
      checkCount: (n: string) => `${n} مورد`,
      jobs: 'کارهای زمان‌بندی‌شده‌ی این سرور',
      jobsNone: 'هیچ کار زمان‌بندی‌شده‌ای روی این سرور روشن نیست.',
      jobNames: {
        'books-reconciliation': 'هم‌خوانی شبانه',
        'data-export-sweep': 'پاک‌سازی خروجی‌های کهنه',
        'practitioner-digest': 'خلاصه‌ی پیامکی پزشک',
      } as Record<string, string>,
      restarts: (n: string) => `${n} بار دوباره راه افتاد`,
      errors: 'خطای سرور در یک ساعت گذشته',
      clientErrors: 'خطای مرورگر کاربران در یک ساعت گذشته',
      errorsHint: 'از ۵ خطا به بالا پایش بیرونی هشدار می‌دهد.',
      version: 'نسخه‌ی مستقر',
      clientIp: 'نشانی شما از دید سرور',
      clientIpPrivate:
        'این نشانی خصوصی است، نه نشانی واقعی شما؛ یعنی TRUST_PROXY روی سرور روشن نیست و محدودیت ورود همه را یکی می‌بیند.',
      sameServer: 'کارها، خطاها و نسخه مال همان سروری است که این صفحه را پاسخ داده است.',
    },
    title: 'کلینیک‌ها',
    create: 'کلینیک تازه',
    createTitle: 'کلینیک تازه',
    createHint: 'نام کلینیک و موبایل مدیر آن را بنویسید. برای مدیر یک لینک تعیین رمز ساخته می‌شود که خودتان به او می‌دهید.',
    clinicName: 'نام کلینیک',
    adminName: 'نام مدیر (اختیاری)',
    adminPhone: 'موبایل مدیر',
    invalid: 'ورودی را بررسی کنید.',
    created: (name: string) => `کلینیک «${name}» ساخته شد.`,
    firstAdmin: 'مدیر کلینیک',
    noName: 'بدون نام',
    noLink: 'این شخص از قبل در سازمان دیگری عضو است؛ با همان رمز خودش وارد می‌شود و برایش لینکی ساخته نشد.',
    openClinic: 'باز کردن کلینیک',
    close: 'بستن',
    total: 'همه‌ی کلینیک‌ها',
    statusActive: 'فعال',
    statusSuspended: 'تعلیق‌شده',
    laserOn: 'با لیزر',
    laserOff: 'بدون لیزر',
    search: 'جست‌وجوی نام یا موبایل مدیر',
    empty: 'هنوز کلینیکی ساخته نشده است.',
    noMatch: 'کلینیکی با این مشخصات نیست.',
    counts: (members: string, patients: string, appointments: string, visits: string) =>
      `${members} عضو، ${patients} کلاینت، ${appointments} نوبت و ${visits} ویزیت در ۳۰ روز گذشته`,
    lastActivity: (when: string) => `آخرین فعالیت: ${when}`,
    noActivity: 'هنوز فعالیتی ثبت نشده است.',
    createdAt: (when: string) => `ساخته شده: ${when}`,
    back: 'همه‌ی کلینیک‌ها',
    notFound: 'این کلینیک پیدا نشد.',
    usage: 'میزان استفاده',
    usageHint: 'فقط شمارش؛ اطلاعات کلاینت‌ها و مبلغ‌ها در این پنل دیده نمی‌شود.',
    members: 'عضو فعال',
    patients: 'کلاینت',
    appointments30: 'نوبت در ۳۰ روز',
    visits30: 'ویزیت در ۳۰ روز',
    edit: 'ویرایش',
    laserSwitch: 'این کلینیک بخش لیزر دارد',
    laserHint: 'خاموش یعنی داده‌ی لیزر برای همه پنهان می‌شود؛ چیزی پاک نمی‌شود.',
    saved: 'ذخیره شد.',
    staff: 'اعضا',
    removed: 'حذف‌شده',
    linkNotAllowed: 'برای کسی که در سازمان دیگری هم عضو است لینک ساخته نمی‌شود.',
    suspend: 'تعلیق کلینیک',
    activate: 'فعال‌سازی کلینیک',
    suspendTitle: (name: string) => `کلینیک «${name}» معلق شود؟`,
    activateTitle: (name: string) => `کلینیک «${name}» فعال شود؟`,
    suspendHint: 'تا فعال‌سازی دوباره هیچ‌کس نمی‌تواند وارد این کلینیک شود. هیچ داده‌ای پاک نمی‌شود.',
    activateHint: 'اعضای کلینیک دوباره می‌توانند وارد شوند.',
    suspended: 'کلینیک معلق شد.',
    activated: 'کلینیک فعال شد.',
    suspendedNote: 'این کلینیک معلق است و کسی نمی‌تواند واردش شود.',
    deleteTitle: 'حذف دائمی کلینیک',
    deleteHint:
      'همه‌ی داده‌ی این کلینیک (کلاینت‌ها، نوبت‌ها، پرونده‌ها، انبار، دفتر مالی و گزارش رویدادهایش) برای همیشه پاک می‌شود و برنمی‌گردد. حساب کاربری اعضا می‌ماند.',
    deleteOnlySuspended: 'فقط کلینیکِ تعلیق‌شده حذف می‌شود؛ اول آن را تعلیق کنید.',
    deleteStep1: '۱. خروجی کامل این کلینیک را بگیرید و جای امنی نگه دارید.',
    deleteExport: 'دریافت خروجی کامل',
    deleteExported: 'خروجی دریافت شد.',
    deleteStep2: (name: string) => `۲. برای تأیید، نام کلینیک را دقیقاً بنویسید: «${name}»`,
    deleteName: 'نام کلینیک',
    deleteButton: 'حذف دائمی',
  },
  /** The central GTIN catalog in the platform panel (ADR-055). */
  catalog: {
    title: 'بانک GTIN',
    subtitle: 'بارکدهایی که کلینیک‌ها وارد کرده‌اند. هر بارکد فقط پس از تأیید شما برای همه‌ی کلینیک‌ها استاندارد می‌شود.',
    tabs: { pending: 'منتظر بررسی', approved: 'تأییدشده', rejected: 'ردشده', products: 'کالاهای استاندارد' },
    search: 'جست‌وجوی بارکد، نام یا برند',
    emptyPending: 'پیشنهادی منتظر بررسی نیست.',
    emptyApproved: 'هنوز بارکدی تأیید نشده است.',
    emptyRejected: 'بارکد ردشده‌ای نیست.',
    emptyProducts: 'هنوز کالایی در بانک نیست.',
    noMatch: 'موردی با این مشخصات پیدا نشد.',
    clinicCount: (n: string) => `${n} کلینیک`,
    firstAt: (when: string) => `نخستین پیشنهاد: ${when}`,
    possibleDuplicate: 'شاید تکراری',
    gtinCount: (n: string) => `${n} بارکد`,
    gtin: 'بارکد (GTIN)',
    submissionStatus: { pending: 'منتظر بررسی', approved: 'تأییدشده', rejected: 'ردشده', withdrawn: 'پس گرفته شد' },
    productStatus: { active: 'فعال', retired: 'بایگانی‌شده' },
    notice:
      'بارکدِ معتبر فقط شکلِ درست دارد و اصالتِ کالا را ثابت نمی‌کند. کالاها را هرگز با شباهت نام یکی نکنید: حجم، دوز، لیدوکائین یا تعداد بسته‌ی متفاوت یعنی بارکد و کالای متفاوت.',
    // review panel
    reviewTitle: 'بررسی بارکد',
    proposals: 'پیشنهاد کلینیک‌ها',
    proposalOf: (n: string) => `پیشنهاد ${n}`,
    clinicUnknown: 'کلینیک نامشخص',
    proposedAt: (when: string) => `ثبت: ${when}`,
    nameFa: 'نام فارسی',
    nameEn: 'نام انگلیسی',
    clinicCategory: 'دسته‌ی کلینیک',
    unit: 'واحد',
    brand: 'برند',
    model: 'مدل دقیق',
    category: 'دسته',
    categoryPick: 'انتخاب دسته',
    content: 'حجم یا دوز',
    contentHint: 'همان‌طور که روی جعبه نوشته، مثلاً 1 ml یا 500 U',
    lidocaine: 'لیدوکائین',
    lidocaineUnknown: 'نامشخص',
    lidocaineYes: 'دارد',
    lidocaineNo: 'ندارد',
    packType: 'نوع بسته',
    packCount: 'تعداد در بسته',
    packCountInvalid: 'تعداد در بسته یک عدد صحیح (۱ تا ۱۰۰۰) است.',
    notStated: 'نوشته نشده',
    formTitle: 'مشخصات کالای استاندارد',
    formHint: 'پیش‌پر از آخرین پیشنهاد؛ پیش از تأیید با جعبه‌ی کالا مقایسه و اصلاح کنید.',
    approve: 'تأیید و افزودن به بانک',
    approved: 'بارکد تأیید شد و به بانک اضافه شد.',
    linkTitle: 'اتصال به کالای موجود',
    linkHint: 'اگر این بارکد، بسته یا نسخه‌ی دیگرِ کالایی است که در بانک هست، به همان وصل کنید. بارکد جدید به همان کالا افزوده می‌شود.',
    linkOpen: 'اتصال به کالای موجود…',
    linkPick: 'کالای مقصد',
    linkReason: 'دلیل اتصال',
    linkSubmit: 'اتصال بارکد',
    linked: 'بارکد به کالا وصل شد.',
    suggested: 'پیشنهاد (هم‌برند و هم‌مدل)',
    searchProducts: 'جست‌وجوی کالا در بانک',
    pickNone: 'کالایی انتخاب نشده است.',
    pickChoose: 'انتخاب',
    pickChosen: 'انتخاب‌شده',
    rejectOpen: 'رد کردن…',
    rejectTitle: 'رد بارکد',
    rejectReason: 'دلیل رد',
    rejectHint: 'کلینیک دلیل را می‌بیند. انبار کلینیک دست نمی‌خورد و بارکد برای خودش کار می‌کند.',
    rejectSubmit: 'رد بارکد',
    rejected: 'بارکد رد شد.',
    productOfGtin: 'کالای استاندارد این بارکد',
    closed: 'این بارکد دیگر منتظر بررسی نیست؛ فهرست را دوباره بخوانید.',
    history: 'تاریخچه',
    noHistory: 'هنوز رویدادی نیست.',
    actions: {
      approved: 'تأیید',
      linked: 'اتصال به کالا',
      rejected: 'رد',
      created: 'ساخت کالا',
      edited: 'ویرایش',
      retired: 'بایگانی',
      restored: 'خروج از بایگانی',
      merged: 'یکی کردن',
    },
    by: (name: string) => `توسط ${name}`,
    reasonLine: (reason: string) => `دلیل: ${reason}`,
    // products
    newProduct: 'افزودن کالا',
    newProductTitle: 'کالای تازه در بانک',
    productTitle: 'کالای استاندارد',
    filterStatus: 'وضعیت',
    filterCategory: 'دسته',
    all: 'همه',
    gtins: 'بارکدها',
    noGtin: 'بدون بارکد',
    created: 'کالا به بانک اضافه شد.',
    save: 'ذخیره‌ی تغییر',
    saved: 'تغییر ذخیره شد.',
    retire: 'بایگانی کالا',
    restore: 'خروج از بایگانی',
    retireHint: 'کالای بایگانی‌شده برای کلینیک‌های تازه پیشنهاد نمی‌شود و بارکدی به آن وصل نمی‌شود.',
    retired: 'کالا بایگانی شد.',
    restoredOk: 'کالا از بایگانی خارج شد.',
    reason: 'دلیل',
    reasonRequired: 'دلیل را بنویسید (دست‌کم ۳ نویسه).',
    merge: 'یکی کردن با کالای دیگر',
    mergeHint: 'بارکدهای این کالا به کالای مقصد می‌روند و این کالا بایگانی می‌شود. برگشت ندارد؛ فقط وقتی دو ردیف یک کالا هستند.',
    mergeTarget: 'کالای مقصد',
    mergeSubmit: 'یکی کردن',
    mergeNeedTarget: 'کالای مقصد را انتخاب کنید.',
    merged: 'کالاها یکی شدند.',
    replaceExactTitle: 'با تأیید، این ردیف‌های بی‌GTIN از فهرست پیش‌فرض برداشته می‌شوند',
    replaceExactHint: 'نام یا مدل این کالاها پس از نرمال‌سازی دقیقاً با همین کالا برابر است.',
    replaceNearTitle: 'نام‌های نزدیک در فهرست پیش‌فرض',
    replaceNearHint: 'اگر این ردیف‌ها همان کالا هستند، تیک بزنید تا با این کالای GTIN‌دار جایگزین شوند.',
    replacedCount: (n: string) => `${n} ردیف بی‌GTIN با این کالا جایگزین شد.`,
  },
  /** The platform-managed default item list and clinic request queue (ADR-057). */
  defaultItems: {
    title: 'فهرست پیش‌فرض کالا',
    subtitle: 'کالاهای رایجی که هنگام تعریف کالای تازه در کلینیک‌ها پیشنهاد می‌شوند. انتخاب از فهرست فقط فرم کلینیک را پیش‌پر می‌کند.',
    tabs: {
      items: 'فهرست',
      requests: 'کالاهای تازه‌ی کلینیک‌ها',
      dismissed: 'اضافه‌نشده',
      removed: 'برداشته‌شده',
    },
    search: 'جست‌وجوی نام فارسی، نام انگلیسی یا بارکد…',
    filterCategory: 'دسته',
    filterGtin: 'بارکد (GTIN)',
    all: 'همه',
    withGtin: 'با GTIN',
    withoutGtin: 'بی GTIN',
    emptyItems: 'هنوز کالایی در فهرست پیش‌فرض نیست.',
    emptyRequests: 'کالای تازه‌ای از کلینیک‌ها منتظر بررسی نیست.',
    emptyDismissed: 'ردیف اضافه‌نشده‌ای نیست.',
    emptyRemoved: 'ردیف برداشته‌شده‌ای نیست.',
    noMatch: 'موردی با این مشخصات پیدا نشد.',
    addItem: 'افزودن کالا',
    addItemTitle: 'افزودن کالا به فهرست پیش‌فرض',
    itemTitle: 'کالای پیش‌فرض',
    editTitle: 'ویرایش مشخصات در فهرست پیش‌فرض',
    nameFa: 'نام فارسی',
    nameEn: 'نام انگلیسی',
    category: 'دسته',
    categoryPick: 'انتخاب دسته',
    unit: 'واحد',
    gtin: 'بارکد (GTIN)',
    gtinOptionalHint: 'اگر بارکد دارد وارد کنید تا به‌عنوان کالای استاندارد در بانک GTIN و فهرست پیش‌فرض ثبت شود؛ وگرنه خالی بگذارید.',
    productLinkedNote: 'این ردیف به کالای استاندارد بانک GTIN وصل است و نام، دسته، واحد و بارکدش از بانک خوانده می‌شود.',
    editInGtinBank: 'ویرایش در بانک GTIN',
    addGtinOpen: 'افزودن GTIN',
    addGtinTitle: 'ثبت بارکد (GTIN) و تبدیل به کالای استاندارد',
    addGtinHint: 'با ثبت بارکد، کالای استاندارد در بانک GTIN ساخته می‌شود و جایگزین این ردیف بی‌GTIN در فهرست پیش‌فرض می‌گردد.',
    removeOpen: 'برداشتن از فهرست',
    removeTitle: 'برداشتن از فهرست پیش‌فرض',
    removeHint: 'این ردیف دیگر در فرم کالای تازه‌ی کلینیک‌ها پیشنهاد نمی‌شود. کالاهای قبلی کلینیک‌ها تغییر نمی‌کند.',
    removeReason: 'دلیل برداشتن (اختیاری)',
    removeSubmit: 'برداشتن از فهرست',
    restoreSubmit: 'بازگرداندن',
    added: 'کالا به فهرست پیش‌فرض اضافه شد.',
    saved: 'تغییرات ذخیره شد.',
    removedOk: 'ردیف از فهرست پیش‌فرض برداشته شد.',
    restoredOk: 'ردیف به فهرست پیش‌فرض بازگردانده شد.',
    replacedBy: (name: string) => `جایگزین با «${name}»`,
    removedByAdmin: 'برداشته به دست مدیر',
    removedAt: (when: string) => `برداشته در ${when}`,
    sources: {
      seed: 'فهرست اولیه',
      platform: 'افزوده‌ی مدیر',
      catalog: 'بانک GTIN',
      clinic_request: 'پیشنهاد کلینیک‌ها',
    },
    sourceLabel: 'منبع',
    history: 'تاریخچه',
    noHistory: 'هنوز رویدادی ثبت نشده است.',
    actions: {
      added: 'افزودن به فهرست',
      edited: 'ویرایش مشخصات',
      removed: 'برداشتن از فهرست',
      restored: 'بازگرداندن به فهرست',
      replaced: 'جایگزین‌شده با کالای GTIN‌دار',
      request_added: 'افزودن از پیشنهاد کلینیک‌ها',
      request_dismissed: 'رد پیشنهاد کلینیک‌ها (اضافه نشد)',
    },
    by: (name: string) => `توسط ${name}`,
    reasonLine: (reason: string) => `دلیل: ${reason}`,
    // clinic requests
    requestTitle: 'پیشنهاد کلینیک‌ها برای فهرست پیش‌فرض',
    clinicCount: (n: string) => `${n} کلینیک`,
    requestCount: (n: string) => `${n} مورد`,
    firstAt: (when: string) => `نخستین ثبت: ${when}`,
    lastAt: (when: string) => `آخرین ثبت: ${when}`,
    priorDismissedBadge: (when: string) => `قبلاً اضافه نشد (${when})`,
    priorDismissedNote: (count: string, reason: string | null) =>
      reason ? `این نام پیش‌تر (${count} مورد) با دلیل «${reason}» اضافه نشده بود و کلینیک دیگری دوباره آن را ثبت کرده است.` : `این نام پیش‌تر (${count} مورد) اضافه نشده بود و کلینیک دیگری دوباره آن را ثبت کرده است.`,
    dismissedReasonLine: (reason: string | null, when: string | null) =>
      reason ? `اضافه نشد (${when ?? ''}) · دلیل: ${reason}` : `اضافه نشد${when ? ` (${when})` : ''}`,
    proposalsTitle: 'نام‌ها و مشخصات ثبت‌شده در کلینیک‌ها',
    clinicUnknown: 'کلینیک',
    clinicCategory: 'دسته‌ی نوشته‌شده',
    notStated: 'نوشته نشده',
    gtinAlertTitle: 'همراه این نام، بارکد (GTIN) هم ثبت شده است:',
    gtinAlertHint: 'تأیید بارکد در بانک GTIN کالا را همراه بارکدش به فهرست پیش‌فرض می‌آورد و این درخواست را خودکار می‌بندد.',
    openInGtinBank: 'بررسی در بانک GTIN',
    gtinStatus: {
      approved: 'تأییدشده در بانک',
      pending: 'منتظر بررسی در بانک',
      rejected: 'ردشده در بانک',
      unknown: 'ثبت‌نشده در بانک',
    },
    addFromRequestTitle: 'مشخصات در فهرست پیش‌فرض (بدون GTIN)',
    addFromRequestHint: 'پیش‌پر از آخرین ثبت کلینیک؛ پیش از افزودن به فهرست، نام و دسته را بررسی کنید.',
    addFromRequestSubmit: 'افزودن به فهرست',
    addedFromRequest: 'کالا به فهرست پیش‌فرض اضافه شد.',
    dismissOpen: 'اضافه نشود…',
    dismissTitle: 'اضافه نشدن به فهرست پیش‌فرض',
    dismissHint: 'این گروه از صف منتظر خارج می‌شود. اگر در آینده کلینیک دیگری دوباره همین نام را ثبت کند، با یادآوری به صف برمی‌گردد.',
    dismissReason: 'دلیل (اختیاری)',
    dismissSubmit: 'اضافه نشود',
    dismissedOk: 'درخواست بسته شد (اضافه نشد).',
  },
  /** Stock insights in the platform panel (ADR-056). */
  insights: {
    title: 'تحلیل انبار',
    subtitle: 'آمار انبار کلینیک‌هایی که مبنای اشتراکشان ثبت شده؛ بی نام کلاینت، ویزیت، قیمت و تأمین‌کننده.',
    updatedAt: (when: string) => `به‌روز تا ${when}`,
    neverRun: 'هنوز داده‌ای ساخته نشده است؛ ساخت داده هر ۱۵ دقیقه یک بار انجام می‌شود.',
    lastRunFailed: 'آخرین به‌روزرسانی ناموفق بود؛ اعداد ممکن است کهنه باشند.',
    from: 'از تاریخ',
    to: 'تا تاریخ',
    brand: 'برند',
    brandAll: 'همه‌ی برندها',
    category: 'دسته',
    categoryAll: 'همه‌ی دسته‌ها',
    clinic: 'کلینیک',
    clinicAll: 'همه‌ی کلینیک‌ها',
    rangeInvalid: 'بازه‌ی تاریخ درست نیست (حداکثر دو سال).',
    noSharing: 'هنوز هیچ کلینیکی داده‌اش را به اشتراک نگذاشته است.',
    noSharingHint:
      'پلتفرم باید مبنای قراردادیِ هر کلینیک را در صفحه‌ی همان کلینیک ثبت کند (بخش «اشتراک داده‌ی انبار»)؛ تا آن زمان هیچ آماری ساخته نمی‌شود.',
    openClinics: 'رفتن به کلینیک‌ها',
    kpi: {
      clinicsTitle: 'کلینیک‌ها',
      productsTitle: 'محصولات',
      members: 'کلینیک عضو',
      sharing: 'کلینیک با اشتراک داده',
      withStock: 'دارای انبار فعال',
      recent: 'دارای ثبت در ۳۰ روز اخیر',
      products: 'محصول متمایز',
      withoutGtin: 'کالای بدون GTIN تأییدشده',
      gtinApproved: 'GTIN تأییدشده',
      gtinPending: 'GTIN منتظر بررسی',
    },
    kpiNote: {
      withoutGtin: 'فقط شمارش؛ نام‌ها یکی نمی‌شوند.',
      gtinPending: 'در بانک GTIN',
    },
    totals: 'جمع هر واحد',
    totalsHint: 'مقدار فقط در یک واحد جمع می‌شود؛ ویال هرگز با میلی‌لیتر جمع نمی‌شود.',
    unit: 'واحد',
    inbound: 'ورود',
    inboundNote: 'خرید و موجودی اولیه',
    purchase: 'خرید',
    opening: 'موجودی اولیه',
    openingNote: 'خرید نیست',
    consumption: 'مصرف واقعی',
    consumptionNote: 'ویزیت تکمیل‌شده',
    adjustment: 'اصلاح',
    adjustmentIn: 'اصلاح افزایشی',
    adjustmentOut: 'اصلاح کاهشی',
    returnToSupplier: 'مرجوعی',
    damage: 'ضایعات',
    expiryWriteoff: 'منقضی',
    onHand: 'موجودی',
    usable: 'قابل مصرف',
    keptForClients: 'نگه‌داشته برای کلاینت',
    nearExpiry: 'نزدیک انقضا',
    expired: 'منقضی‌شده',
    low: 'کمبود',
    chartTitle: 'روند ماهانه',
    chartUnit: 'واحد نمودار',
    chartTable: 'جدول اعداد',
    chartEmpty: 'در این بازه ورود یا مصرفی ثبت نشده است.',
    chartLabel: (unit: string) => `ورود و مصرف واقعی هر ماه به ${unit}`,
    month: 'ماه',
    closing: 'موجودی پایان ماه',
    topProducts: 'پرمصرف‌ترین محصول‌ها',
    topBrands: 'پرمصرف‌ترین برندها',
    clinicsOf: (n: string) => `${n} کلینیک`,
    hiddenNote: 'محصول یا برندی که فقط از کمتر از ۳ کلینیک می‌آید نشان داده نمی‌شود تا جمع شبکه کلینیکی را لو ندهد.',
    emptyTop: 'در این بازه مصرفی نیست.',
    products: 'محصولات',
    productsEmpty: 'محصولی با این فیلتر نیست.',
    productsTruncated: 'فقط بخشی از محصول‌ها نشان داده شده؛ بازه یا فیلتر را محدودتر کنید.',
    product: 'محصول',
    gtin: 'GTIN',
    clinics: 'کلینیک',
    unitMismatch: 'واحد ناهمسان',
    unitMismatchHint: 'این محصول در کلینیک‌ها با چند واحد ثبت شده؛ هر واحد ردیف جدا دارد.',
    csvNetwork: 'دریافت CSV شبکه',
    csvClinic: 'دریافت CSV این کلینیک',
    csvHint: 'برای دریافت فایل، رمز یا کد را دوباره تأیید می‌کنید و دریافت در گزارش رویدادها می‌ماند.',
    csvDone: 'فایل CSV دریافت شد.',
    openClinic: 'جزئیات کلینیک',
    // one clinic
    clinicTitle: 'تحلیل انبار',
    back: 'تحلیل انبار',
    itemsTitle: 'کالاهای کلینیک',
    itemsEmpty: 'کالایی ثبت نشده است.',
    item: 'کالا',
    noGtinShort: 'بدون GTIN',
    periodConsumption: 'مصرف بازه',
    previousConsumption: 'بازه‌ی پیش از آن',
    nearestExpiry: 'نزدیک‌ترین انقضا',
    archived: 'بایگانی‌شده',
    itemSeries: 'روند ماهانه‌ی کالا',
    movementsTable: 'جدول حرکت‌های ماهانه',
    periodTotals: 'جمع بازه',
    itemSeriesEmpty: 'برای این کالا در بازه حرکتی نیست.',
    // sharing card
    sharing: {
      title: 'اشتراک داده‌ی انبار',
      hint: 'تعیین می‌کند آمار انبار این کلینیک در تحلیل پلتفرم دیده شود یا نه. مبنا (مثلاً شماره‌ی قرارداد) باید ثبت شود؛ کلینیک آن را در تنظیمات خودش می‌بیند.',
      levels: { none: 'خاموش', aggregate: 'فقط در جمع شبکه', detailed: 'با جزئیات کلینیک' },
      levelNotes: {
        none: 'آمار این کلینیک هیچ‌جا نمی‌آید.',
        aggregate: 'فقط داخل جمع شبکه می‌آید و جزئیاتش باز نمی‌شود.',
        detailed: 'در جمع شبکه می‌آید و صفحه‌ی جزئیات و CSV خودش را هم دارد.',
      },
      level: 'سطح اشتراک',
      basis: 'مبنای قراردادی',
      basisHint: 'مثلاً شماره‌ی قرارداد یا نسخه‌ی شرایطی که پذیرفته شده',
      effectiveFrom: 'تاریخ شروع',
      effectiveHint: 'تاریخچه‌ی انبار پیش از این روز استفاده نمی‌شود.',
      updatedAt: (when: string) => `آخرین تغییر: ${when}`,
      noBasis: 'ثبت نشده',
      noDate: 'ثبت نشده',
      save: 'ذخیره‌ی اشتراک',
      saved: 'اشتراک داده ذخیره شد.',
      openInsights: 'دیدن تحلیل انبار این کلینیک',
      secure: 'ذخیره پشت تأیید دوباره‌ی رمز است و در گزارش رویدادها می‌ماند.',
    },
  },
  passwordLink: {
    setup: 'لینک تعیین رمز',
    reset: 'بازنشانی رمز',
    waiting: 'منتظر تعیین رمز',
    title: 'لینک تعیین رمز',
    titleReset: 'لینک بازنشانی رمز',
    explainSetup: 'این لینک را به خود او بدهید (حضوری یا با پیام‌رسان خودتان). با باز کردنش رمزش را می‌سازد و وارد می‌شود.',
    explainReset: 'رمز فعلی او تا وقتی این لینک استفاده نشده کار می‌کند. با استفاده از لینک، از همه‌ی دستگاه‌ها بیرون می‌رود و رمز تازه می‌سازد.',
    warning: 'این لینک مثل رمز است: فقط به همان شخص بدهید و جایی منتشرش نکنید.',
    expires: (when: string) => `معتبر تا ${when}، یک‌بارمصرف. لینک تازه لینک قبلی را باطل می‌کند.`,
    copy: 'رونوشت لینک',
    copied: 'لینک رونوشت شد.',
    share: 'فرستادن',
    make: 'ساخت لینک',
    confirmReset: 'برای این عضو لینک بازنشانی رمز ساخته شود؟',
  },
  onboarding: {
    title: 'ساخت کلینیک',
    subtitle: 'هنوز عضو هیچ کلینیکی نیستید. کلینیک خود را بسازید یا از مدیر کلینیک بخواهید شما را اضافه کند.',
    waitingTitle: 'هنوز عضو کلینیکی نیستید',
    waitingBody: 'از مدیر کلینیک بخواهید شما را با همین شماره اضافه کند؛ پس از آن، این صفحه را تازه کنید.',
    refresh: 'تازه کردن',
    displayName: 'نام و نام خانوادگی شما',
    clinicName: 'نام کلینیک',
    submit: 'ساخت کلینیک',
  },
  org: {
    choose: 'انتخاب سازمان',
    chooseHint: 'در بیش از یک سازمان عضو هستید. یکی را انتخاب کنید.',
    notMember: 'به این سازمان دسترسی ندارید.',
    home: 'خانه',
    welcome: 'به مدکسا خوش آمدید',
    yourRoles: 'نقش‌های شما',
    nextPhase: 'بخش‌های کلاینت، نوبت، انبار و مالی در فاز بعد (Clinic Core) اضافه می‌شوند.',
    switch: 'تغییر سازمان',
    orgType: { platform: 'پلتفرم', clinic: 'کلینیک', supplier: 'تأمین‌کننده' },
  },
  members: {
    title: 'اعضا',
    empty: 'هنوز عضوی ثبت نشده است.',
    add: 'افزودن عضو',
    name: 'نام و نام خانوادگی',
    nameHint: 'اگر این شخص از قبل حساب و نام دارد، نام خودش می‌ماند.',
    phone: 'شماره‌ی موبایل',
    roles: 'نقش‌ها',
    submitAdd: 'افزودن',
    /** The admin decides how far a secretary reaches the stock (ADR-053). */
    inventoryAccess: {
      label: 'دسترسی به انبار',
      view: 'فقط دیدن موجودی',
      receive: 'تعریف و ورود کالا، بی قیمت خرید',
      receiveCost: 'تعریف و ورود کالا، با قیمت خرید',
      hint: 'بی قیمت خرید: قیمت را مدیر یا پزشک بعداً در «منتظر قیمت خرید» می‌نویسد. بعدها از «دسترسی» همین عضو هم عوض می‌شود.',
      failed: 'عضو افزوده شد ولی دسترسی انبار ذخیره نشد؛ از «دسترسی» او دوباره بزنید.',
    },
    editRoles: 'تغییر نقش‌ها',
    remove: 'حذف از سازمان',
    confirmRemove: 'حذف این عضو تأیید شود؟',
    confirm: 'تأیید حذف',
    you: 'شما',
    noName: 'بی‌نام',
    added: 'عضو اضافه شد.',
    rolesSaved: 'نقش‌ها ذخیره شد.',
    laserRolesHint:
      'منشی لیزر فقط نوبت‌ها و صندوق لیزر را می‌بیند (در تنظیمات قابل تغییر است). اپراتور لیزر فقط ویزیت‌های لیزر را انجام و ثبت می‌کند.',
    laserOff: 'لیزر خاموش',
    access: {
      open: 'دسترسی‌ها',
      title: (name: string) => `دسترسی‌های ${name}`,
      custom: 'دسترسی تعیین‌شده',
      hint: 'هر تیک یک بخش از کار است. پیش‌فرض از نقش‌ها می‌آید؛ با تغییر نقش، تیک‌ها به پیش‌فرض نقش تازه برمی‌گردند. مدیریت اعضا و انجام ویزیت همیشه از نقش است.',
      fromRole: 'پیش‌فرض نقش',
      medicalWarning:
        'این عضو پروفایل پزشکی، یادداشت‌ها و جزئیات درمان کلاینت‌ها را می‌بیند و می‌نویسد. هر بار دیدن در گزارش رویدادها ثبت می‌شود. برای دادن آن، رمز شما دوباره پرسیده می‌شود.',
      reset: 'بازگشت به پیش‌فرض نقش',
      saved: 'دسترسی‌ها ذخیره شد.',
      areas: {
        patients: 'پرونده‌ی پایه‌ی کلاینت‌ها',
        medical: 'پرونده‌ی پزشکی',
        appointments: 'نوبت‌ها و پذیرش',
        sms: 'پیامک‌ها',
        cash: 'صندوق',
        finance: 'مالی',
        inventory: 'انبار: دیدن',
        inventory_receive: 'انبار: ورود کالا',
        inventory_cost: 'انبار: قیمت خرید',
        inventory_trace: 'انبار: ردیابی تا کلاینت',
        inventory_manage: 'انبار: مدیریت',
        settings: 'تنظیمات کلینیک',
        data: 'ورود و خروج داده',
        audit: 'گزارش رویدادها',
      } satisfies Record<AccessArea, string>,
      areaHints: {
        patients: 'نام، تماس و مشخصات؛ ساخت و ویرایش کلاینت',
        medical: 'پروفایل پزشکی، یادداشت‌ها و جزئیات درمان',
        appointments: 'تقویم، نوبت‌دهی، پذیرش و فهرست امروز',
        sms: 'صف پیامک‌ها و فرستادن',
        cash: 'مانده‌ی کلاینت، دریافت وجه و فروش پکیج',
        finance: 'گزارش‌ها، هزینه‌ها، بستانکاران، برگشت ثبت و سهم اعضا',
        inventory: 'کالاها، موجودی و هشدارها',
        inventory_receive: 'تعریف کالای تازه و ثبت رسید کالا',
        inventory_cost: 'دیدن و نوشتن قیمت خرید کالا؛ بی آن رسید بی قیمت ثبت می‌شود',
        inventory_trace: 'یافتن کلاینت‌هایی که از یک Lot مصرف کرده‌اند',
        inventory_manage: 'ویرایش و بایگانی کالا، اصلاح و خروج موجودی',
        settings: 'خدمات، اتاق‌ها، پکیج‌ها، پیامک‌ها، نوبت‌دهی و بخش لیزر',
        data: 'ورود کلاینت‌ها از فایل و خروجی کامل',
        audit: 'دیدن همین گزارش',
      } satisfies Record<AccessArea, string>,
    },
    removed: 'عضو حذف شد. برای بازگرداندن، دوباره او را اضافه کنید.',
  },
  audit: {
    title: 'گزارش رویدادها',
    empty: 'رویدادی ثبت نشده است.',
    emptyCategory: 'رویدادی از این نوع ثبت نشده است.',
    actor: 'انجام‌دهنده',
    system: 'سیستم',
    category: 'نوع رویداد',
    allCategories: 'همه‌ی رویدادها',
    when: 'زمان',
    entity: 'مربوط به',
    openEntity: 'باز کردن',
    changes: 'تغییرها',
    field: 'مورد',
    before: 'قبل',
    after: 'بعد',
    value: 'مقدار',
    noChanges: 'جزئیات تغییر برای این رویداد ثبت نمی‌شود.',
    requestId: 'شناسه‌ی درخواست',
    ip: 'نشانی IP',
    yes: 'بله',
    no: 'خیر',
    values: { active: 'فعال', removed: 'حذف‌شده', archived: 'بایگانی‌شده' } as Record<string, string>,
    entities: {
      patient: 'کلاینت',
      appointment: 'نوبت',
      visit: 'ویزیت',
      membership: 'عضو',
      user: 'کاربر',
      session: 'نشست',
      organization: 'کلینیک',
      clinic: 'تنظیمات کلینیک',
      service: 'خدمت',
      resource: 'اتاق یا دستگاه',
      service_package: 'پکیج',
      clinical_note: 'یادداشت بالینی',
      journal_entry: 'ثبت مالی',
      vendor: 'تأمین‌کننده',
      inventory_item: 'کالا',
      stock_batch: 'بچ کالا',
      treatment_method: 'متد درمان',
      practitioner_settings: 'تنظیم پزشک',
      sms_delivery: 'پیامک',
      sms_template: 'قالب پیامک',
      patient_import: 'ورود کلاینت‌ها',
      data_export: 'خروجی کامل',
      finance_export: 'خروجی دفتر مالی',
      prior_service: 'خدمت قبلی',
    } as Record<string, string>,
    fields: {
      status: 'وضعیت',
      name: 'نام',
      displayName: 'نام نمایشی',
      roles: 'نقش‌ها',
      areas: 'دسترسی‌ها',
      medicalGranted: 'پرونده‌ی پزشکی داده شد',
      firstName: 'نام',
      lastName: 'نام خانوادگی',
      phone: 'موبایل',
      nationalCode: 'کد ملی',
      gender: 'جنسیت',
      birthDate: 'تاریخ تولد',
      address: 'نشانی',
      maritalStatus: 'وضعیت تأهل',
      referralSource: 'نحوه‌ی آشنایی',
      priceRial: 'قیمت',
      amountRial: 'مبلغ',
      purchaseRial: 'مبلغ خرید',
      reversedRial: 'مبلغ برگشتی',
      durationMinutes: 'مدت (دقیقه)',
      recallAfterDays: 'فاصله‌ی تمدید (روز)',
      touchUp: 'ترمیم دارد',
      touchUpDays: 'روز ترمیم',
      recallOn: 'موعد تمدید',
      touchUpOn: 'موعد ترمیم',
      performedOn: 'تاریخ انجام',
      section: 'بخش',
      startsAt: 'شروع',
      endsAt: 'پایان',
      reason: 'دلیل',
      note: 'یادداشت',
      method: 'روش',
      type: 'نوع',
      from: 'از',
      to: 'تا',
      rows: 'تعداد ردیف',
      category: 'دسته',
      unit: 'واحد',
      lowThreshold: 'کمبود',
      gtin: 'بارکد کالا',
      qtyOnHand: 'موجودی',
      expiresAt: 'انقضا',
      expiresOn: 'انقضا',
      isActive: 'فعال',
      allowOverlap: 'نوبت هم‌زمان',
      allowAdvancePayment: 'بیعانه',
      checkInRequired: 'پذیرش لازم',
      laserEnabled: 'مرکز لیزر',
      laserDeskRoles: 'نوبت‌های لیزر با',
      laserCashRoles: 'صندوق لیزر با',
      includesClinical: 'شامل داده‌ی بالینی',
      purpose: 'هدف',
      sessionsClosed: 'نشست‌های بسته‌شده',
      body: 'متن',
      changedFields: 'موارد تغییرکرده',
      named: 'نام داده شد',
      userId: 'شناسه‌ی کاربر',
      custom: 'تعیین‌شده با تیک',
      conditions: 'PMH (بیماری‌ها)',
      otherConditions: 'PMH (سایر)',
      allergies: 'حساسیت‌ها',
      medications: 'DH (داروها)',
      notes: 'نکات پزشکی',
      priorInjections: 'سابقه‌ی تزریق زیبایی',
      priorInjectionTypes: 'تزریق‌های قبلی',
      priorInjectionsOther: 'سایر تزریق‌ها',
    } as Record<string, string>,
    categories: {
      finance: 'مالی',
      appointments: 'نوبت‌ها',
      visits: 'ویزیت‌ها',
      patients: 'کلاینت‌ها و پرونده‌ی پزشکی',
      inventory: 'انبار',
      messages: 'پیامک‌ها',
      settings: 'تنظیمات (خدمات، اتاق‌ها، متدها)',
      access: 'اعضا و ورود',
      data: 'ورود و خروج داده',
    } satisfies Record<AuditCategory, string>,
  },
  roles: {
    platform_admin: 'مدیر پلتفرم',
    clinic_admin: 'مدیر کلینیک',
    doctor: 'پزشک',
    receptionist: 'منشی کلینیک',
    laser_receptionist: 'منشی لیزر',
    laser_operator: 'اپراتور لیزر',
    supplier_admin: 'مدیر تأمین‌کننده',
    supplier_staff: 'کارمند تأمین‌کننده',
  } satisfies Record<Role, string>,
  auditActions: {
    'auth.login': 'ورود به حساب',
    'auth.logout': 'خروج از حساب',
    'auth.refresh_reuse_detected': 'تشخیص استفاده‌ی مجدد نشست',
    'auth.session_revoked': 'ابطال نشست',
    'org.created': 'ساخت سازمان',
    'membership.created': 'افزودن عضو',
    'membership.reactivated': 'بازگرداندن عضو',
    'membership.roles_changed': 'تغییر نقش‌ها',
    'membership.named': 'نام‌گذاری عضو',
    'membership.access_changed': 'تغییر دسترسی عضو',
    'membership.removed': 'حذف عضو',
    'patient.created': 'ثبت کلاینت',
    'patient.updated': 'ویرایش پرونده‌ی پایه‌ی کلاینت',
    'patient.archived': 'بایگانی کلاینت',
    'patient.restored': 'خروج کلاینت از بایگانی',
    'medical_profile.viewed': 'مشاهده‌ی پروفایل پزشکی',
    'medical_profile.updated': 'ویرایش پروفایل پزشکی',
    'clinical_notes.viewed': 'مشاهده‌ی یادداشت‌های بالینی',
    'clinical_note.created': 'ثبت یادداشت بالینی',
    'service.created': 'تعریف خدمت',
    'service.updated': 'ویرایش خدمت',
    'service.archived': 'بایگانی خدمت',
    'service.restored': 'خروج خدمت از بایگانی',
    'resource.created': 'تعریف اتاق یا دستگاه',
    'resource.updated': 'ویرایش اتاق یا دستگاه',
    'resource.archived': 'بایگانی اتاق یا دستگاه',
    'resource.restored': 'خروج اتاق یا دستگاه از بایگانی',
    'appointment.created': 'ثبت نوبت',
    'appointment.rescheduled': 'جابه‌جایی نوبت',
    'appointment.status_changed': 'تغییر وضعیت نوبت',
    'visit.opened': 'شروع ویزیت',
    'visit.item_added': 'ثبت خدمت در ویزیت',
    'visit.item_updated': 'ویرایش خدمت ویزیت',
    'visit.item_removed': 'حذف خدمت از ویزیت',
    'visit.completed': 'تکمیل ویزیت',
    'visit.voided': 'ابطال ویزیت',
    'visit.follow_up_changed': 'تغییر موعد تمدید یا ترمیم',
    'prior_service.recorded': 'ثبت خدمت قبلی',
    'prior_service.follow_up_changed': 'تغییر موعد خدمت قبلی',
    'prior_service.voided': 'ابطال خدمت قبلی',
    'visit.clinical_viewed': 'مشاهده‌ی جزئیات درمان',
    'sms.marked': 'ثبت نتیجه‌ی پیامک',
    'sms.sent': 'ارسال پیامک از پنل پیامکی',
    'sms_template.updated': 'ویرایش قالب پیامک',
    'treatment_method.created': 'ساخت متد درمان',
    'treatment_method.updated': 'ویرایش متد درمان',
    'treatment_method.archived': 'بایگانی متد درمان',
    'inventory_item.created': 'تعریف کالا',
    'inventory_item.updated': 'ویرایش کالا',
    'inventory_item.archived': 'بایگانی کالا',
    'inventory_item.restored': 'خروج کالا از بایگانی',
    'stock.received': 'رسید ورود کالا',
    'stock.cost_recorded': 'ثبت قیمت خرید',
    'stock.adjusted': 'اصلاح موجودی',
    'stock.written_off': 'خروج کالا از انبار',
    'stock.reservation_released': 'آزاد کردن ویال نگه‌داشته',
    'inventory_settings.updated': 'تغییر آستانه‌های انقضا',
    'visit.material_added': 'ثبت ماده‌ی مصرفی ویزیت',
    'visit.material_updated': 'ویرایش ماده‌ی مصرفی ویزیت',
    'visit.material_removed': 'حذف ماده‌ی مصرفی ویزیت',
    'journal.payment_recorded': 'ثبت دریافت از کلاینت',
    'journal.discount_recorded': 'ثبت تخفیف',
    'journal.refund_recorded': 'بازپرداخت به کلاینت',
    'journal.adjustment_recorded': 'اصلاح مانده‌ی کلاینت',
    'journal.opening_balance_recorded': 'ثبت مانده‌ی افتتاحیه',
    'journal.expense_recorded': 'ثبت هزینه',
    'journal.entry_reversed': 'برگشت یک ثبت مالی',
    'visit.financially_reversed': 'ابطال مالی ویزیت',
    'share_rule.created': 'قاعده‌ی سهم کارکرد',
    'service_package.created': 'تعریف پکیج',
    'service_package.updated': 'ویرایش پکیج',
    'service_package.archived': 'بایگانی پکیج',
    'service_package.restored': 'خروج پکیج از بایگانی',
    'patient_package.sold': 'فروش پکیج',
    'patient_package.cancelled': 'لغو پکیج',
    'auth.reauthenticated': 'تأیید دوباره‌ی هویت',
    'patients.imported': 'ورود کلاینت‌ها از فایل',
    'data_export.requested': 'درخواست خروجی کامل داده',
    'data_export.downloaded': 'دانلود خروجی کامل داده',
    'finance_export.downloaded': 'دانلود خروجی دفتر مالی (CSV)',
    'practitioner_settings.updated': 'تغییر اجازه‌ی نوبت هم‌زمان پزشک',
    'booking_settings.updated': 'تغییر تنظیم پذیرش کلاینت',
    'section_settings.updated': 'تغییر دسترسی بخش لیزر',
    'patient_package.extended': 'تمدید پکیج',
    'finance_settings.updated': 'تغییر تنظیم بیعانه',
    'vendor.created': 'تعریف تأمین‌کننده',
    'vendor.updated': 'ویرایش تأمین‌کننده',
    'vendor.archived': 'بایگانی تأمین‌کننده',
    'vendor.restored': 'خروج تأمین‌کننده از بایگانی',
    'journal.vendor_payment_recorded': 'پرداخت به تأمین‌کننده',
    'journal.vendor_opening_recorded': 'ثبت بدهی پیشین به تأمین‌کننده',
    'auth.password_link_issued': 'ساخت لینک تعیین رمز',
    'auth.password_set': 'تعیین رمز با لینک',
    'auth.password_changed': 'تغییر رمز',
  } satisfies Record<AuditAction, string>,
  dataImport: {
    title: 'ورود کلاینت‌ها از فایل',
    open: 'ورود از فایل',
    subtitle: 'فایل Excel یا CSV کلاینت‌ها را بیاورید. پیش از ورود، نتیجه‌ی هر ردیف را می‌بینید.',
    steps: { file: 'فایل', mapping: 'ستون‌ها', preview: 'پیش‌نمایش', done: 'نتیجه' },
    drop: 'فایل Excel یا CSV را اینجا رها کنید',
    choose: 'انتخاب فایل',
    limits: 'برگه‌ی اول فایل خوانده می‌شود؛ حداکثر ۵۰۰۰ ردیف در هر ورود.',
    template: 'دانلود نمونه‌ی خالی',
    templateName: 'نمونه-ورود-کلاینت‌ها.csv',
    reading: 'در حال خواندن فایل…',
    unreadable: 'این فایل خوانده نشد. آن را در Excel با قالب Excel Workbook یا CSV ذخیره کنید.',
    noRows: 'فایل ردیفی ندارد.',
    tooMany: (n: string) => `این فایل ${n} ردیف دارد؛ حداکثر ۵۰۰۰ ردیف در هر ورود. فایل را چند بخش کنید.`,
    fileSummary: (rows: string, columns: string) => `${rows} ردیف و ${columns} ستون`,
    changeFile: 'فایل دیگر',
    mappingTitle: 'هر فیلد از کدام ستون خوانده شود؟',
    mappingHint: 'از روی عنوان ستون‌ها پیشنهاد شده است؛ در صورت نیاز عوض کنید.',
    firstRowHeader: 'ردیف اول عنوان ستون‌هاست',
    notInFile: 'در فایل نیست',
    column: (n: string) => `ستون ${n}`,
    sample: 'نمونه',
    nameRequired: 'یکی از ستون‌های «نام» یا «نام کامل» را انتخاب کنید.',
    medicalGroup: 'پروفایل پزشکی',
    medicalNeedsDoctor: 'ستون‌های پزشکی را فقط عضوی که نقش پزشک دارد وارد می‌کند.',
    balanceUnit: 'واحد مبلغ مانده',
    units: { toman: 'تومان', rial: 'ریال' },
    balanceHint: 'مانده‌ی بدهی هر کلاینت تازه یک «مانده‌ی افتتاحیه» در دفتر می‌شود.',
    preview: 'پیش‌نمایش',
    totals: {
      rows: 'ردیف',
      create: 'وارد می‌شود',
      duplicate: 'تکراری',
      invalid: 'نامعتبر',
      withWarnings: 'با هشدار',
      balance: 'جمع مانده‌ی بدهی',
      balanceRows: (n: string) => `برای ${n} کلاینت`,
    },
    outcomes: { create: 'تازه', duplicate: 'تکراری', invalid: 'نامعتبر' },
    filterAll: 'همه',
    filterWarnings: 'با هشدار',
    row: (n: string) => `ردیف ${n}`,
    noName: 'بی‌نام',
    duplicateOfPatient: (by: string) => `همان کلاینت موجود (${by})`,
    duplicateOfRow: (row: string, by: string) => `تکرار ردیف ${row} (${by})`,
    duplicateBy: { external_ref: 'شناسه‌ی قبلی', file_number: 'شماره‌ی پرونده', national_code: 'کد ملی', phone_name: 'موبایل و نام' },
    errors: {
      required: 'خالی است',
      too_long: 'بیش از حد بلند است',
      invalid_phone: 'شماره‌ی موبایل معتبر نیست',
      invalid_national_code: 'کد ملی معتبر نیست',
      invalid_gender: 'جنسیت شناخته نشد',
      invalid_birth_date: 'تاریخ تولد معتبر نیست',
      invalid_referral_source: 'منبع آشنایی شناخته نشد',
      invalid_amount: 'مبلغ عدد نیست',
      negative_amount: 'مانده‌ی منفی (بستانکار) در ورود پذیرفته نمی‌شود',
    } as Record<string, string>,
    warnings: {
      shared_phone: 'این موبایل با نام دیگری هم ثبت است',
      national_code_padded: 'صفرهای ابتدای کد ملی افزوده شد',
      unknown_condition: 'بیماری ناشناخته در «سایر بیماری‌ها» ثبت می‌شود',
      balance_not_imported: 'تکراری است؛ مانده‌اش وارد نمی‌شود',
    } as Record<string, string>,
    fields: {
      externalRef: 'شناسه‌ی قبلی',
      fileNumber: 'شماره‌ی پرونده',
      firstName: 'نام',
      lastName: 'نام خانوادگی',
      fullName: 'نام کامل',
      phone: 'موبایل',
      nationalCode: 'کد ملی',
      gender: 'جنسیت',
      birthDate: 'تاریخ تولد',
      referralSource: 'منبع آشنایی',
      openingBalance: 'مانده‌ی بدهی',
      allergies: 'حساسیت‌ها',
      conditions: 'بیماری‌های زمینه‌ای',
      medications: 'داروهای مصرفی',
      medicalNotes: 'یادداشت پزشکی',
    },
    fieldHints: {
      externalRef: 'شناسه‌ی کلاینت در سیستم قبلی؛ ورود دوباره‌ی همان فایل را بی‌اثر می‌کند.',
      fileNumber: 'شماره‌ی پرونده‌ی کلاینت در کلینیک؛ روی پرونده‌ی کلاینت دیده می‌شود.',
      fullName: 'اگر نام و نام خانوادگی در یک ستون است؛ در آخرین فاصله جدا می‌شود.',
      birthDate: 'شمسی یا میلادی.',
    } as Record<string, string>,
    commit: (n: string) => `ورود ${n} کلاینت`,
    nothingNew: 'کلاینت تازه‌ای در این فایل نیست.',
    rulesNote:
      'ردیف تکراری و نامعتبر وارد نمی‌شود و کلاینت موجود تغییر نمی‌کند. فایل را درست کنید و دوباره بیاورید؛ ردیف‌های واردشده این بار «تکراری» دیده می‌شوند.',
    staleNote: 'ستون‌ها یا فایل عوض شد؛ دوباره پیش‌نمایش بگیرید.',
    done: (n: string) => `${n} کلاینت وارد شد.`,
    doneBalance: (amount: string, n: string) => `${amount} مانده‌ی بدهی برای ${n} کلاینت در دفتر ثبت شد.`,
    another: 'ورود فایل دیگر',
    toPatients: 'رفتن به کلاینت‌ها',
    history: 'ورودهای قبلی',
    historyEmpty: 'هنوز ورودی انجام نشده است.',
    historyLine: (created: string, duplicates: string, invalid: string) =>
      `${created} وارد شد، ${duplicates} تکراری، ${invalid} نامعتبر`,
    by: (name: string) => `به‌دست ${name}`,
    openPatient: 'پرونده',
    showMore: 'نمایش ردیف‌های بیشتر',
  },
  dataExport: {
    title: 'خروجی کامل داده‌ی کلینیک',
    hint: 'همه‌ی داده‌ی کلینیک در یک فایل ZIP از جدول‌های CSV، برای نگهداری یا انتقال به سیستم دیگر. فایل ۲۴ ساعت می‌ماند و فقط سازنده‌اش آن را دانلود می‌کند.',
    clinicalIncluded: 'پروفایل پزشکی و یادداشت‌های بالینی هم در خروجی می‌آیند، چون نقش پزشک دارید.',
    clinicalExcluded:
      'پروفایل پزشکی و یادداشت‌های بالینی در خروجی نمی‌آیند؛ فقط عضوی با نقش پزشک آن‌ها را می‌گیرد.',
    secure: 'این فایل اطلاعات شخصی و پزشکی کلاینت‌ها را دارد؛ آن را امن نگه دارید و پس از استفاده پاک کنید.',
    create: 'ساختن خروجی',
    download: 'دانلود',
    empty: 'هنوز خروجی ساخته نشده است.',
    statuses: { queued: 'در صف', running: 'در حال ساخت', ready: 'آماده', failed: 'ناموفق', expired: 'منقضی' },
    requestedBy: (name: string) => `درخواست: ${name}`,
    files: (n: string) => `${n} فایل`,
    until: (time: string) => `قابل دانلود تا ${time}`,
    excluded: (n: string) => `${n} جدول بالینی حذف شد`,
    notMine: 'فقط درخواست‌کننده دانلود می‌کند.',
    kilobytes: (n: string) => `${n} کیلوبایت`,
    megabytes: (n: string) => `${n} مگابایت`,
  },
  workingHours: {
    title: 'ساعت کاری',
    hint: 'پیشنهاد «اولین زمان خالی» برای نوبت فقط داخل همین روزها و ساعت‌ها دنبال زمان می‌گردد. روزی که تعطیل است در پیشنهاد نمی‌آید.',
    days: ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'],
    until: 'تا',
    closed: 'تعطیل',
    copyToAll: 'کپی به همه‌ی روزها',
    copyHint: 'ساعت اولین روزِ باز را به همه‌ی روزها می‌دهد؛ پس از آن هر روز را جدا هم می‌توانید عوض کنید.',
    openAria: (day: string) => `${day} باز است`,
    fromAria: (day: string) => `ساعت شروع ${day}`,
    toAria: (day: string) => `ساعت پایان ${day}`,
    problems: { endBeforeStart: 'ساعت پایان باید پس از ساعت شروع باشد.' },
    saved: 'ساعت کاری ذخیره شد.',
    readOnly: 'فقط مدیر کلینیک ساعت کاری را عوض می‌کند.',
    closedDays: {
      title: 'روزهای تعطیل',
      hint: 'تعطیلی رسمی یا روزی که کلینیک باز نیست. این روزها در پیشنهاد «اولین زمان خالی» نمی‌آیند و در تقویم مشخص می‌شوند.',
      add: 'افزودن روز',
      pick: 'روز تعطیل',
      remove: (day: string) => `حذف ${day}`,
      empty: 'روز تعطیلی ثبت نشده است.',
      saved: 'روزهای تعطیل ذخیره شد.',
    },
    addShift: 'اضافه کردن شیفت دیگر',
    addShiftHint: 'اگر صبح و عصر ساعت جدا دارید یا هر پزشک ساعت خودش را دارد، شیفت دیگری بسازید و نامش را بنویسید. هر زمانی که در یکی از شیفت‌ها باز باشد برای نوبت پیشنهاد می‌شود.',
    maxShifts: 'حداکثر ۱۰ شیفت می‌توان داشت.',
    shiftLabel: (n: number) => `شیفت ${n}`,
    shiftName: 'نام شیفت',
    shiftNameHint: 'مثلاً «شیفت صبح»، «شیفت عصر» یا «شیفت دکتر ...».',
    removeShift: (name: string) => `حذف ${name}`,
    nameProblems: { empty: 'نام شیفت را بنویسید.', duplicate: 'این نام برای شیفت دیگری هم هست.' },
    inShift: (day: string, name: string) => `${day}، ${name}`,
  },
  tour: {
    counter: (n: string, total: string) => `${n} از ${total}`,
    next: 'بعدی',
    back: 'قبلی',
    finish: 'پایان',
    skip: 'رد کردن راهنما',
  },
  welcomeTour: {
    title: 'راهنمای شروع',
    menu: 'راهنمای شروع',
    later: 'بعداً',
    startTour: 'شروع راهنمای صفحه‌ها',
    step: (n: string, total: string) => `مرحله‌ی ${n} از ${total}`,
    next: 'بعدی',
    back: 'قبلی',
    skipStep: 'رد کردن این مرحله',
    finish: 'شروع کار',
    welcome: {
      hello: (name: string) => `سلام ${name}`,
      clinic: (clinic: string) => `به ${clinic} خوش آمدید.`,
      roles: 'نقش شما در این کلینیک',
      intro: 'در چند قدم کوتاه نشان می‌دهیم در اینجا چه کارهایی از دست شما برمی‌آید. هر زمان خواستید، از «راهنمای شروع» دوباره ببینید.',
      adminIntro: 'پیش از آن، چند تنظیم اولیه‌ی کلینیک را با هم انجام می‌دهیم. هر مرحله را می‌توانید رد کنید و بعداً از «تنظیمات» و «اعضا» انجام دهید.',
    },
    laser: {
      title: 'آیا کلینیک شما بخش لیزر دارد؟',
      hint: 'اگر دارد، نوبت‌ها و صندوق لیزر جدا از کلینیک می‌شود و نقش‌های «منشی لیزر» و «اپراتور لیزر» در دسترس است. این را بعداً در «تنظیمات ← بخش لیزر» هم می‌توانید عوض کنید.',
      yes: 'بله، دارد',
      no: 'نه، ندارد',
      group: 'بخش لیزر',
      savedYes: 'بخش لیزر روشن شد.',
      savedNo: 'بخش لیزر خاموش است.',
      more: 'اینکه نوبت و صندوق لیزر را منشی لیزر اداره کند یا منشی کلینیک، در «تنظیمات ← بخش لیزر» است.',
    },
    members: {
      title: 'اعضای تیم را اضافه کنید',
      hint: 'نام، موبایل و نقش هر عضو را بنویسید. برای ورودش، رمز او را بعداً از صفحه‌ی «اعضا» با لینک تعیین رمز می‌سازید.',
      added: 'در این مرحله اضافه شد',
      add: 'افزودن عضو',
      addAnother: 'افزودن عضو دیگر',
      later: 'اعضای بیشتر را هر زمان از «اعضا» اضافه کنید.',
    },
    hours: {
      title: 'ساعت کاری کلینیک',
    },
    steps: {
      today: {
        title: 'امروز در یک نگاه',
        body: 'اینجا نوبت‌های امروز را می‌بینید و کنار هر کدام کاری که باید بعد انجام شود: پذیرش، شروع ویزیت یا ثبت پرداخت. بدون باز کردن نوبت هم می‌شود کار را پیش برد. مدیر اینجا امروزِ کلینیک را در یک نگاه می‌بیند: شمار نوبت‌ها و برنامه‌ی هر درمانگر.',
      },
      calendar: {
        title: 'تقویم روز',
        body: 'ستون هر پزشک نوبت‌های همان روز را نشان می‌دهد. روی یک نوبت بزنید تا جزئیاتش باز شود، و روی خانه‌ی خالی بزنید تا همان ساعت نوبت بدهید.',
      },
      newAppointment: {
        title: 'نوبت تازه',
        body: 'کلاینت و خدمت را انتخاب کنید؛ زمان‌های پر را نشان می‌دهیم و اولین زمان خالی پزشک را پیشنهاد می‌دهیم، فقط داخل ساعت کاری کلینیک.',
      },
      walkIn: {
        title: 'مراجعه‌ی بدون نوبت',
        body: 'کلاینتی که بدون وقت قبلی آمده را همین‌جا ثبت کنید. برایش در اولین زمان خالی امروز نوبت ساخته می‌شود و می‌توانید کار را شروع کنید.',
      },
      patients: {
        title: 'فهرست کلاینت‌ها',
        body: 'کلاینت را با نام یا موبایل پیدا کنید؛ میان‌بر Ctrl K هم جستجوی سریع است. در هر سطر آخرین مراجعه، نوبت بعدی و مانده‌ی حساب کلاینت را می‌بینید.',
      },
      addPatient: {
        title: 'کلاینت تازه',
        body: 'نام، موبایل و کد ملی را بنویسید تا پرونده ساخته شود. کلاینتی که کد ملی ندارد (مثلاً کلاینت خارجی) با تیک «ندارد» ثبت می‌شود.',
      },
      recall: {
        title: 'موعد تمدید',
        body: 'در این زبانه کلاینت‌هایی را می‌بینید که وقت مراجعه‌ی دوباره‌شان رسیده است. از همین‌جا تماس بگیرید یا نوبت بدهید. بازه‌ی آن را مدیر در «تنظیمات» تعیین می‌کند.',
      },
      visit: {
        title: 'ثبت خدمت، مبلغ و مواد',
        body: 'در ویزیت، خدمت انجام‌شده، مبلغ آن برای همین کلاینت و مواد مصرفی را وارد می‌کنید. با تکمیل ویزیت می‌توانید پرداخت و اقساط را همان‌جا بنویسید و انبار و حساب کلاینت خودکار به‌روز می‌شود. ویزیت را از نوبت یا از فهرست امروز شروع می‌کنید.',
      },
      medical: {
        title: 'پرونده‌ی پزشکی',
        body: 'در صفحه‌ی هر کلاینت، سوابق پزشکی، دارویی و تزریقات او را می‌بینید و ثبت می‌کنید. هر بار دیدنِ پرونده در گزارش رویدادها می‌ماند.',
      },
      finance: {
        title: 'مالی',
        body: 'با این زبانه‌ها درآمد، هزینه، بدهکاران و بستانکاران کلینیک را می‌بینید. هیچ ثبت مالی ویرایش نمی‌شود؛ اصلاح فقط با ثبتِ برگشتی است.',
      },
      inventoryReceipt: {
        title: 'رسید کالا',
        body: 'کالایی که خریده‌اید را همین‌جا با مقدار، شماره‌ی بچ و تاریخ انقضای واقعی ثبت کنید تا موجودی و هزینه‌ی انبار درست بماند.',
      },
      settingsHours: {
        title: 'ساعت کاری',
        body: 'روزها و ساعت‌هایی که کلینیک باز است را اینجا تعیین کنید. نوبت‌دهی فقط داخل همین ساعت‌ها پیشنهاد می‌دهد.',
      },
      settingsLaser: {
        title: 'بخش لیزر',
        body: 'اگر کلینیک بخش لیزر دارد، اینجا روشنش کنید و بگویید نوبت‌ها و صندوق آن را منشی لیزر اداره کند یا منشی کلینیک.',
      },
      members: {
        title: 'اعضای تیم',
        body: 'با «افزودن عضو» نام، موبایل و نقش هر همکار را بنویسید. برای ورودش، لینک تعیین رمز را از همین صفحه می‌سازید.',
      },
    },
  },
  stepUp: {
    title: 'تأیید با کد پیامکی',
    passwordTitle: 'تأیید با رمز',
    passwordHint: 'برای این کار، رمز خود را دوباره وارد کنید.',
    /** Around the phone number, which is shown in its own left-to-right isolate. */
    hintBefore: 'برای این کار، کد ۶ رقمی‌ای را که به',
    hintAfter: 'پیامک می‌شود وارد کنید.',
    send: 'ارسال کد',
    code: 'کد تأیید',
    verify: 'تأیید و ادامه',
    resend: 'ارسال دوباره‌ی کد',
  },
} as const;
